import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Autocomplete, IconButton,
  Card, CardContent, Chip, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, Tooltip, CircularProgress, LinearProgress, Divider,
  ToggleButton, ToggleButtonGroup, Badge, Menu, MenuItem, ListItemText, ListItemIcon,
  Checkbox
} from '@mui/material';
import {
  Event as EventIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  MeetingRoom as MeetingRoomIcon,
  Settings as SettingsIcon,
  Balance as BalanceIcon,
  Class as ClassIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  CalendarMonth as CalendarIcon,
  List as ListIcon,
  Download as DownloadIcon,
  Assignment as AssignmentIcon,
  Today as TodayIcon,
  WarningAmber as WarningIcon,
  Notifications as NotificationsIcon,
  Info as InfoIcon,
  CheckCircleOutline as SuccessIcon,
  Sync as SyncIcon,
  Print as PrintIcon,
  AccessTime as AccessTimeIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  PictureAsPdf as PdfIcon,
  Archive as ArchiveIcon,
  AddCircleOutline as NewSessionIcon,
  FolderOpen as SessionIcon,
  Edit as EditIcon,
  AutoFixHigh as RattrapageIcon,
  GridOn as ExcelIcon,
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';

// --- PDF GENERATION IMPORTS ---
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

// --- HELPER: CONVERT BUFFER TO BASE64 ---
const arrayBufferToBase64 = (buffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
};

// --- HELPER: CLEAN MODULE NAME ---
const getCleanModuleName = (exam) => {
    // 1. Priority: Arabic Name
    if (exam.lib_elp_arb) return exam.lib_elp_arb;

    // 2. Fallback: Clean French Name (Deduplicate)
    if (!exam.module_name) return "";

    const parts = exam.module_name.split(/[&+]/).map(s => s.trim());
    const uniqueParts = parts.filter((item, index) => 
        parts.findIndex(p => p.toLowerCase() === item.toLowerCase()) === index
    );

    return uniqueParts.join(' & ');
};

// --- CONSTANTS: PROFESSORS LIST ---
const AVAILABLE_PROFESSORS = [
  "pr.Akkour", "pr.Aloui", "pr.Arrach", "pr.Badr DEHBI", "pr.Belbesbes", "pr.Belkadi",
  "pr.Benbounou", "pr.Benmanssour", "pr.Bouzekraoui", "pr.Brouksy", "pr.Echcharyf",
  "pr.El idrissi", "pr.El jamri", "pr.Es-saheb", "pr.Hiroual", "pr.Jabal", "pr.M.Abed",
  "pr.MLY driss el idrissi", "pr.Maatouk", "pr.Majidi", "pr.Mbarki", "pr.Meftah",
  "pr.Moussadek", "pr.Oualji", "pr.Qorchi", "pr.Rafik", "pr.Razzok", "pr.taybi",
  "ذ. أفقير", "ذ. أوقسو", "ذ. أوهاروش", "ذ. إ.الحافظي", "ذ. البوشيخي", "ذ. البوهالي",
  "ذ. الجمري", "ذ. الحجاجي", "ذ. الحافظي", "ذ. الذهبي", "ذ. الرطيمات", "ذ. الرقاي",
  "ذ. السكتاني", "ذ. الشرغاوي", "ذ. الشداوي", "ذ. الشيكر", "ذ. الصالحي", "ذ. الطيبي",
  "ذ. العاشيري", "ذ. العثماني", "ذ. العلمى", "ذ. العمراني", "ذ. القشتول", "ذ. المحراوي",
  "ذ. المصبحي", "ذ. الملوكي", "ذ. المليحي", "ذ. النوري", "ذ. النوحي", "ذ. ايت العكيد",
  "ذ. بنقاسم", "ذ. بوذياب", "ذ. تملكوتان", "ذ. جبران", "ذ. حاسون", "ذ. حميدا", "ذ. خربوش",
  "ذ. خفيف", "ذ. خلوقي", "ذ. رحو", "ذ. سيتر", "ذ. شحشي", "ذ. صبونجي", "ذ. عباد", "ذ. عراش",
  "ذ. فضيل", "ذ. فلاح", "ذ. قاسمي", "ذ. قصبي", "ذ. كواعروس", "ذ. لبنى المصباحي", "ذ. مالكي",
  "ذ. مكاوي", "ذ. منال النوحي", "ذ. مهم", "ذ. نبيه", "ذ. نعناني", "ذ. نقوب", "ذ. هروال",
  "ذ. وقاص", "ذ. يحياوي", "ذ. يونسي", "ذة. أفقير", "ذة. الجمري", "ذة. الحافظي",
  "ذة. الرطيمات", "ذة. الصالحي", "ذة. القشتول", "ذة. النوري", "ذة. بنقاسم", "ذة. جبران",
  "ذة. فضيل", "ذة. فلاح", "ذة. لبنى المصباحي", "ذة. منال النوحي", "ذة.العلمي", "ذة.بنقاسم",
  "ذة.سميح", "ذة.وقاص"
].sort((a, b) => a.localeCompare(b));

// --- COMPONENT: STAT CARD ---
const StatCard = ({ title, value, icon, color, loading, subtitle, onClick }) => (
  <Card 
    sx={{ 
      height: '100%', 
      boxShadow: 2, 
      position: 'relative', 
      overflow: 'hidden',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.2s',
      '&:hover': onClick ? { transform: 'scale(1.02)', boxShadow: 4 } : {}
    }}
    onClick={onClick}
  >
    <Box sx={{ position: 'absolute', top: -10, right: -10, width: 80, height: 80, borderRadius: '50%', bgcolor: (theme) => theme.palette[color].light, opacity: 0.2 }} />
    <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, '&:last-child': { pb: 2 } }}>
      <Box sx={{ zIndex: 1 }}>
        <Typography color="text.secondary" variant="subtitle2" fontWeight="bold">{title}</Typography>
        {loading ? (
          <CircularProgress size={20} sx={{ mt: 1 }} />
        ) : (
          <>
            <Typography variant="h4" fontWeight="bold" color={`${color}.main`}>{value}</Typography>
            {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
          </>
        )}
      </Box>
      <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: (theme) => theme.palette[color].light, color: (theme) => theme.palette[color].contrastText || theme.palette[color].dark, opacity: 0.8, display: 'flex', zIndex: 1 }}>
        {icon}
      </Box>
    </CardContent>
  </Card>
);

// --- HELPER: Get Color based on Module Code ---
const getExamColor = (moduleCode) => {
    const colors = ['#E3F2FD', '#FFF3E0', '#F3E5F5', '#E8F5E9', '#FFF9C4', '#FCE4EC'];
    const hash = (moduleCode || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
};

// --- HELPER: merge same module_code+group_name rows in a time slot into one card
//     (one exam_planning row per room → one card listing all its rooms) ---
const mergeExamsByModuleGroup = (examsInSlot) => Object.values(examsInSlot.reduce((acc, curr) => {
    const uniqueKey = `${curr.module_code || curr.module_name}_${curr.group_name || 'SansGroupe'}`;
    if (!acc[uniqueKey]) {
        acc[uniqueKey] = {
            ...curr,
            all_locations: [curr.location],
            all_groups: [curr.group_name],
            total_assigned: curr.assigned_count || 0,
            ids: [curr.id]
        };
    } else {
        if (!acc[uniqueKey].all_locations.includes(curr.location)) {
            acc[uniqueKey].all_locations.push(curr.location);
        }
        acc[uniqueKey].total_assigned += (curr.assigned_count || 0);
        acc[uniqueKey].ids.push(curr.id);
        if (!acc[uniqueKey].lib_elp_arb && curr.lib_elp_arb) {
            acc[uniqueKey].lib_elp_arb = curr.lib_elp_arb;
        }
    }
    return acc;
}, {}));

// --- HELPER: "Amphi 2 → 3" or single room name ---
const formatRoomRangeText = (locs) => {
    if (!locs || locs.length === 0) return '—';
    const sorted = [...locs].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    return sorted.length === 1 ? sorted[0] : `${sorted[0]} → ${sorted[sorted.length - 1]}`;
};

// --- HELPER: "G.1/G.2" or "Tous" ---
const formatGroupLabel = (groups) => {
    const filtered = (groups || []).filter(g => g && g !== 'Tous');
    return filtered.length === 0 ? 'Tous' : filtered.join(' / ');
};

// --- HELPER: "09:00" -> "10:00" ---
const addOneHourLabel = (t) => {
    const [h, m] = t.split(':').map(Number);
    return `${String(h + 1).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

// --- HELPER: session's academic_year (e.g. 2026) -> "السنة الجامعية: 2026 / 2027" ---
const formatAcademicYearLabel = (academicYear) => {
    const y = parseInt(academicYear, 10);
    return isNaN(y) ? 'السنة الجامعية' : `السنة الجامعية: ${y} / ${y + 1}`;
};

// --- COMPONENT: EXAM CARD CONTENT ---
const ExamCardContent = ({ exam }) => (
    <>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
             <Typography 
                variant="subtitle1" 
                sx={{ 
                    fontWeight: 'bold', lineHeight: 1.3, color: '#000', fontSize: '11pt', flex: 1, wordBreak: 'break-word',
                    fontFamily: exam.lib_elp_arb ? "'Amiri', 'Arial', sans-serif" : 'inherit'
                }}
             >
                {exam.lib_elp_arb ? exam.lib_elp_arb : exam.module_name}
             </Typography>
        </Box>
        
        {exam.lib_elp_arb && (
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#555', fontStyle: 'italic', fontSize: '9pt', lineHeight: 1.1 }}>
                {exam.module_name}
            </Typography>
        )}

        <Divider sx={{ my: 0.5, borderColor: 'rgba(0,0,0,0.1)' }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                <MeetingRoomIcon sx={{ fontSize: '1rem', color: 'primary.main', mt: 0.3 }} />
                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '10pt', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.3 }}>
                    {exam.all_locations.join(', ')}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                 <ClassIcon sx={{ fontSize: '1rem', color: 'action.active', mt: 0.3 }} />
                 <Typography variant="body2" sx={{ fontSize: '9.5pt', lineHeight: 1.3, color: '#333', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {exam.all_groups.includes('Tous') ? 'Toutes Sections' : exam.all_groups.join(' + ')}
                 </Typography>
            </Box>

            {exam.professor_name && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <PersonIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#e65100', fontSize: '0.85rem' }}>
                        {exam.professor_name}
                    </Typography>
                </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                <Chip label={`Fin: ${exam.end_time.substring(0,5)}`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                {exam.total_assigned > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PeopleIcon sx={{ fontSize: '1rem', color: 'action.active' }} />
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                            {exam.total_assigned} étds
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    </>
);

// --- COMPONENT: DAILY PLANNING VIEW ---
const DailyPlanningView = ({
  exams, onSelectExam, selectedExams, onToggleSelect, readOnly, onEditExam, sessionLabel,
  currentSession, globalStats, conflictCount,
}) => {
  const uniqueDates = useMemo(() => {
    const dates = [...new Set(exams.map(e => e.exam_date.split('T')[0]))];
    return dates.sort((a, b) => new Date(a) - new Date(b));
  }, [exams]);

  // All distinct start times across the whole view — becomes the printed grid's columns
  const allTimes = useMemo(() => {
    const set = new Set(exams.map(e => e.start_time.substring(0, 5)));
    return [...set].sort();
  }, [exams]);

  const getExamsForDate = (dateStr) => {
    return exams.filter(e => e.exam_date.startsWith(dateStr)).sort((a, b) => a.start_time.localeCompare(b.start_time));
  };

  // ── GENERATE ENVELOPES PDF FOR ONE DAY — 1 page per room ──
  const generateDayEnvelopes = async (dateStr, dayExams) => {
    const sorted = [...dayExams].sort((a, b) => {
      const t = a.start_time.localeCompare(b.start_time);
      return t !== 0 ? t : (a.location || '').localeCompare(b.location || '', undefined, { numeric: true });
    });
    if (sorted.length === 0) return;

    // Build range map: session key → all sorted locations
    const rangeMap = {};
    sorted.forEach(exam => {
      const k = `${exam.start_time}|${exam.module_code || exam.module_name}|${exam.group_name}`;
      if (!rangeMap[k]) rangeMap[k] = [];
      if (exam.location && !rangeMap[k].includes(exam.location)) rangeMap[k].push(exam.location);
    });
    Object.values(rangeMap).forEach(locs =>
      locs.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    );

    const doc = new jsPDF({ orientation: 'landscape', format: 'a4', unit: 'mm' });
    const W = 297;

    const fontBytes = await fetch('/fonts/Amiri-Regular.ttf').then(r => r.arrayBuffer());
    doc.addFileToVFS('Amiri-Regular.ttf', arrayBufferToBase64(fontBytes));
    doc.addFont('Amiri-Regular.ttf', 'Amiri', 'normal');

    let logo1 = null, logo2 = null;
    try {
      logo1 = arrayBufferToBase64(await fetch('/logo1.png').then(r => r.arrayBuffer()));
      logo2 = arrayBufferToBase64(await fetch('/logo2.png').then(r => r.arrayBuffer()));
    } catch { /* logos optional */ }

    const dateLabel = new Date(dateStr + 'T00:00:00').toLocaleDateString('fr-FR', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });

    // Extract group numbers as an array: "1(JLAD6405) + 2(JLAD6405)" → ["1","2"]
    const extractGroupNums = (g) => {
      if (!g || g === 'Tous') return null; // null = show "الكل"
      const nums = g.split('+').map(p => p.trim().match(/\d+/)?.[0]).filter(Boolean);
      return nums.length > 0 ? nums : null;
    };

    // Render group numbers centered in lCx — each part drawn independently to bypass bidi
    const drawGroupNums = (g, cx, y) => {
      const nums = extractGroupNums(g);
      if (!nums) {
        // "Tous" case — Arabic text
        doc.setFont('Amiri', 'normal');
        doc.setFontSize(38); doc.setTextColor(10, 110, 10);
        doc.text('الكل', cx, y, { align: 'center' });
        return;
      }
      if (nums.length === 1) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(58); doc.setTextColor(10, 110, 10);
        doc.text(nums[0], cx, y, { align: 'center' });
        doc.setFont('Amiri', 'normal');
        return;
      }
      // Multiple numbers: build parts array [num, " و ", num, " و ", num ...]
      const parts = [];
      nums.forEach((n, i) => {
        parts.push({ text: n, font: 'helvetica', style: 'bold', size: 52 });
        if (i < nums.length - 1) parts.push({ text: ' - ', font: 'helvetica', style: 'bold', size: 52 });
      });
      // Measure total width
      let totalW = 0;
      parts.forEach(p => {
        doc.setFont(p.font, p.style);
        doc.setFontSize(p.size);
        totalW += doc.getTextWidth(p.text);
      });
      // Draw each part left-to-right from the calculated start X
      let curX = cx - totalW / 2;
      parts.forEach(p => {
        doc.setFont(p.font, p.style);
        doc.setFontSize(p.size);
        doc.setTextColor(10, 110, 10);
        const w = doc.getTextWidth(p.text);
        doc.text(p.text, curX, y);
        curX += w;
      });
      doc.setFont('Amiri', 'normal');
    };

    sorted.forEach((exam, i) => {
      if (i > 0) doc.addPage();
      doc.setFont('Amiri', 'normal');

      // ── HEADER: logos + السنة/الدورة only ──
      if (logo1) doc.addImage(logo1, 'PNG', 8, 5, 27, 27);
      if (logo2) doc.addImage(logo2, 'PNG', W - 35, 5, 27, 27);

      doc.setFontSize(11); doc.setTextColor(60, 60, 60);
      doc.text(formatAcademicYearLabel(currentSession?.academic_year), W / 2, 13, { align: 'center' });
      doc.text(sessionLabel || 'الدورة: الخريفية الـــعـــاديــــة', W / 2, 21, { align: 'center' });

      doc.setDrawColor(130); doc.setLineWidth(0.5);
      doc.line(12, 33, W - 12, 33);

      // ── DATE — prominent below divider ──
      doc.setFontSize(18); doc.setTextColor(30, 30, 30);
      doc.text(dateLabel, W / 2, 43, { align: 'center' });

      // ── MODULE NAME ──
      const arabic = exam.lib_elp_arb || '';
      const french = getCleanModuleName(exam) || exam.module_name || '';

      doc.setTextColor(0);
      if (arabic) {
        doc.setFontSize(36);
        doc.text(arabic, W / 2, 57, { align: 'center' });
        if (french) {
          doc.setFontSize(10); doc.setTextColor(110, 110, 110);
          doc.text(french, W / 2, 65, { align: 'center' });
        }
      } else {
        doc.setFontSize(24); doc.text(french, W / 2, 57, { align: 'center' });
      }

      // ── TIME — big, centered ──
      const startT = exam.start_time.substring(0, 5);
      const endT   = exam.end_time.substring(0, 5);
      doc.setFontSize(30); doc.setTextColor(0, 65, 150);
      doc.text(`${startT}  —  ${endT}`, W / 2, 77, { align: 'center' });

      // ── DETAILS BOX ──
      // Structure: top section (الفوج | المدرج) with vertical divider
      //            horizontal divider
      //            bottom section: الأستاذ full-width centered
      const bX = 12, bY = 83, bW = W - 24, bH = 76;
      const topH = 52; // height of الفوج/المدرج section
      doc.setDrawColor(160); doc.setLineWidth(0.6);
      doc.rect(bX, bY, bW, bH);

      // Vertical divider — top section only
      const divX = bX + bW * 0.38;
      const lCx  = bX + bW * 0.19;
      const rCx  = divX + (bX + bW - divX) / 2;
      doc.setLineWidth(0.4);
      doc.line(divX, bY, divX, bY + topH);

      // Horizontal divider — separates top from professor strip
      doc.setLineWidth(0.35); doc.setDrawColor(190);
      doc.line(bX, bY + topH, bX + bW, bY + topH);
      doc.setDrawColor(160);

      const locName = exam.location || '—';
      const profStr = exam.professor_name || '....................................';

      // ── LEFT: الفوج label + numbers (drawn per-part to avoid bidi reversal) ──
      doc.setFont('Amiri', 'normal');
      doc.setFontSize(15); doc.setTextColor(80, 80, 80);
      doc.text('الفوج', lCx, bY + 12, { align: 'center' });

      drawGroupNums(exam.group_name, lCx, bY + 46);

      // ── RIGHT: المدرج label + room name ──
      doc.setFontSize(15); doc.setTextColor(80, 80, 80);
      doc.text('المدرج', rCx, bY + 12, { align: 'center' });

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(38); doc.setTextColor(0, 0, 0);
      doc.text(locName, rCx, bY + 42, { align: 'center' });
      doc.setFont('Amiri', 'normal');

      // ── BOTTOM STRIP: الأستاذ — full width, centered ──
      const stripMidY = bY + topH + (bH - topH) / 2; // vertical center of strip
      doc.setFontSize(12); doc.setTextColor(120, 0, 0);
      doc.text('الأستاذ(ة)', W / 2, stripMidY - 4, { align: 'center' });

      doc.setFontSize(15); doc.setTextColor(180, 0, 0);
      const isLatinProf = /^[a-zA-Z\s.]/.test(profStr.trim());
      if (isLatinProf) doc.setFont('helvetica', 'normal');
      doc.text(profStr, W / 2, stripMidY + 6, { align: 'center' });
      doc.setFont('Amiri', 'normal');

      // ── BOTTOM: range (center) + module code (left) + student count (right) ──
      const bBot = bY + bH + 8;

      // Range arrow — drawn with helvetica bold + manual arrowhead
      const rangeKey = `${exam.start_time}|${exam.module_code || exam.module_name}|${exam.group_name}`;
      const sessLocs = rangeMap[rangeKey] || [exam.location];
      if (sessLocs.length > 1) {
        const loc1 = sessLocs[0];
        const loc2 = sessLocs[sessLocs.length - 1];
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14); doc.setTextColor(60, 60, 60);
        const tw1 = doc.getTextWidth(loc1);
        const tw2 = doc.getTextWidth(loc2);
        const span = 20;
        const rx   = (W - (tw1 + span + tw2)) / 2;
        doc.text(loc1, rx, bBot);
        doc.text(loc2, rx + tw1 + span, bBot);
        const lx1 = rx + tw1 + 3, lx2 = rx + tw1 + span - 5, ly = bBot - 1.6;
        doc.setLineWidth(0.7); doc.setDrawColor(60, 60, 60);
        doc.line(lx1, ly, lx2, ly);
        doc.setFillColor(60, 60, 60);
        doc.triangle(lx2, ly - 2, lx2, ly + 2, lx2 + 4.5, ly, 'F');
        doc.setFont('Amiri', 'normal');
      }

      doc.setFontSize(8); doc.setTextColor(150, 150, 150);
      // Show full group/code info (e.g. "1(JLAD6405) + 2(JLAD6405)"), fallback to module_code
      const bottomInfo = (exam.group_name && exam.group_name !== 'Tous')
        ? exam.group_name
        : (exam.module_code || '');
      doc.text(bottomInfo, bX, bBot, { align: 'left' });
      doc.text(`${exam.assigned_count ?? '—'} étudiant(s)`, bX + bW, bBot, { align: 'right' });
    });

    doc.save(`enveloppes_${dateStr}.pdf`);
  };

  // ── GENERATE FULL PLANNING GRID PDF — rows = days, columns = time slots,
  //     matching the reference layout (real jsPDF table, not a browser print) ──
  const generatePlanningGridPDF = async () => {
    if (uniqueDates.length === 0) return;

    const doc = new jsPDF({ orientation: 'landscape', format: 'a4', unit: 'mm' });
    const W = 297;

    const fontBytes = await fetch('/fonts/Amiri-Regular.ttf').then(r => r.arrayBuffer());
    doc.addFileToVFS('Amiri-Regular.ttf', arrayBufferToBase64(fontBytes));
    doc.addFont('Amiri-Regular.ttf', 'Amiri', 'normal');
    doc.setFont('Amiri', 'normal');

    let logo1 = null, logo2 = null;
    try {
      logo1 = arrayBufferToBase64(await fetch('/logo1.png').then(r => r.arrayBuffer()));
      logo2 = arrayBufferToBase64(await fetch('/logo2.png').then(r => r.arrayBuffer()));
    } catch { /* logos optional */ }

    const margin = { left: 8, right: 8, top: 26 };
    const DAY_COL_W = 30;
    const timeColW = (W - margin.left - margin.right - DAY_COL_W) / Math.max(allTimes.length, 1);

    // Merged exams per (day, time) cell, precomputed once
    const grid = uniqueDates.map(dateStr => {
      const dayExams = getExamsForDate(dateStr);
      return allTimes.map(t => mergeExamsByModuleGroup(dayExams.filter(e => e.start_time.substring(0, 5) === t)));
    });

    // Real plain-text content drives autoTable's row-height calculation; the rich
    // colored redraw happens in didDrawCell below, within the same allotted space.
    const cellText = (exams) => exams.map(exam => {
      const lines = [exam.lib_elp_arb || exam.module_name || ''];
      if (exam.professor_name) lines.push(`(${exam.professor_name})`);
      lines.push(`${formatGroupLabel(exam.all_groups)} — ${formatRoomRangeText(exam.all_locations)}`);
      return lines.join('\n');
    }).join('\n———\n');

    const headerRow = ['', ...allTimes.map(t => `${t} → ${addOneHourLabel(t)}`)];
    const bodyRows = uniqueDates.map((dateStr, di) => {
      const dateObj = new Date(dateStr + 'T00:00:00');
      const label = dateObj.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
      return [label, ...grid[di].map(exams => cellText(exams))];
    });

    const columnStyles = {
      0: { fillColor: [232, 240, 250], textColor: [20, 20, 20], fontStyle: 'bold', fontSize: 7.5, halign: 'center', valign: 'middle', cellWidth: DAY_COL_W },
    };
    allTimes.forEach((_, i) => { columnStyles[i + 1] = { cellWidth: timeColW }; });

    doc.autoTable({
      startY: margin.top,
      margin: { left: margin.left, right: margin.right },
      head: [headerRow],
      body: bodyRows,
      theme: 'grid',
      styles: {
        font: 'Amiri', fontSize: 6.5, valign: 'top', halign: 'center',
        textColor: [20, 20, 20], lineColor: [130, 130, 130], lineWidth: 0.2, cellPadding: 1,
      },
      headStyles: { fillColor: [25, 118, 210], textColor: 255, fontSize: 8, halign: 'center', valign: 'middle' },
      columnStyles,
      didDrawPage: () => {
        if (logo1) doc.addImage(logo1, 'PNG', margin.left, 4, 16, 16);
        if (logo2) doc.addImage(logo2, 'PNG', W - margin.right - 16, 4, 16, 16);
        doc.setFont('Amiri', 'normal');
        doc.setFontSize(12); doc.setTextColor(20, 20, 20);
        doc.text('برنامج امتحانات', W / 2, 10, { align: 'center' });
        doc.setFontSize(9); doc.setTextColor(90, 90, 90);
        doc.text(sessionLabel || '', W / 2, 16, { align: 'center' });
        doc.setFontSize(8); doc.setTextColor(110, 110, 110);
        doc.text(formatAcademicYearLabel(currentSession?.academic_year), W / 2, 21, { align: 'center' });
      },
      willDrawCell: (data) => {
        if (data.section === 'body' && data.column.index > 0) {
          data.cell.text = []; // suppress default render — replaced below with richer styling
        }
      },
      didDrawCell: (data) => {
        if (data.section !== 'body' || data.column.index === 0) return;
        const di = data.row.index;
        const ti = data.column.index - 1;
        const exams = grid[di]?.[ti];
        if (!exams || exams.length === 0) return;

        const { x, y, width } = data.cell;
        let cursorY = y + 3;
        exams.forEach((exam, idx) => {
          const title = exam.lib_elp_arb || exam.module_name || '';
          doc.setFont('Amiri', 'normal');
          doc.setFontSize(6.4);
          doc.setTextColor(0, 0, 0);
          doc.splitTextToSize(title, width - 3).forEach(line => {
            doc.text(line, x + width / 2, cursorY, { align: 'center' });
            cursorY += 2.5;
          });

          if (exam.professor_name) {
            doc.setFontSize(5.3); doc.setTextColor(170, 0, 0);
            doc.splitTextToSize(`(${exam.professor_name})`, width - 3).forEach(line => {
              doc.text(line, x + width / 2, cursorY, { align: 'center' });
              cursorY += 2.2;
            });
          }

          doc.setFont('helvetica', 'bold');
          doc.setFontSize(5.8); doc.setTextColor(10, 110, 10);
          doc.text(formatGroupLabel(exam.all_groups), x + width / 2, cursorY, { align: 'center' });
          cursorY += 2.3;

          doc.setFont('Amiri', 'normal');
          doc.setFontSize(6.2); doc.setTextColor(0, 0, 0);
          doc.text(formatRoomRangeText(exam.all_locations), x + width / 2, cursorY, { align: 'center' });
          cursorY += 3;

          if (idx < exams.length - 1) {
            doc.setDrawColor(200); doc.setLineWidth(0.15);
            doc.line(x + 1, cursorY - 1, x + width - 1, cursorY - 1);
            cursorY += 1.5;
          }
        });
      },
    });

    doc.save(`planning_examens_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  // ── GENERATE PLANNING EXCEL — exam list + stats for the CURRENTLY SELECTED session only ──
  const generatePlanningExcel = () => {
    if (exams.length === 0) return;

    const wb = XLSX.utils.book_new();

    // Sheet 1: Info + Stats for the selected session
    const infoRows = [
      ['Session', currentSession?.name || '—'],
      ['Type', currentSession?.session_type || '—'],
      ['Année universitaire', currentSession?.academic_year || '—'],
      ['Statut', currentSession?.status || '—'],
      ['Session active', currentSession?.is_current ? 'Oui' : 'Non'],
      ['Généré le', new Date().toLocaleString('fr-FR')],
      [],
      ['Statistiques', ''],
      ['Total examens', globalStats?.totalSessions ?? exams.length],
      ['Étudiants planifiés', globalStats?.totalAssigned ?? exams.reduce((s, e) => s + (e.assigned_count || 0), 0)],
      ['Modules uniques', globalStats?.uniqueModules ?? new Set(exams.map(e => e.module_code)).size],
      ["Jours d'examens", globalStats?.uniqueDays ?? 0],
      ['Conflits horaire (étudiants)', conflictCount ?? 0],
    ];
    const infoSheet = XLSX.utils.aoa_to_sheet(infoRows);
    infoSheet['!cols'] = [{ wch: 28 }, { wch: 40 }];
    XLSX.utils.book_append_sheet(wb, infoSheet, 'Info & Statistiques');

    // Sheet 2: flat exam list
    const header = ['Module', 'Code', 'Groupe', 'Date', 'Début', 'Fin', 'Local', 'Professeur', 'Étudiants assignés'];
    const sorted = [...exams].sort((a, b) => {
      const d = a.exam_date.localeCompare(b.exam_date);
      return d !== 0 ? d : a.start_time.localeCompare(b.start_time);
    });
    const dataRows = sorted.map(e => [
      e.lib_elp_arb || e.module_name || e.module_code,
      e.module_code,
      e.group_name || 'Tous',
      e.exam_date ? e.exam_date.split('T')[0] : '',
      e.start_time?.substring(0, 5) || '',
      e.end_time?.substring(0, 5) || '',
      e.location || '—',
      e.professor_name || '—',
      e.assigned_count ?? 0,
    ]);
    const planningSheet = XLSX.utils.aoa_to_sheet([header, ...dataRows]);
    planningSheet['!cols'] = [
      { wch: 42 }, { wch: 14 }, { wch: 16 }, { wch: 12 },
      { wch: 8 }, { wch: 8 }, { wch: 16 }, { wch: 22 }, { wch: 16 },
    ];
    XLSX.utils.book_append_sheet(wb, planningSheet, 'Planning');

    const safeName = (currentSession?.name || 'session').replace(/[^a-z0-9]+/gi, '_');
    XLSX.writeFile(wb, `planning_${safeName}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <style>{`
          @media print {
            @page { size: landscape; margin: 5mm 8mm; }
            body * { visibility: hidden; }
            #daily-planning-container, #daily-planning-container * { visibility: visible; }
            #daily-planning-container { position: absolute; left: 0; top: 0; width: 100%; }
            body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; background-color: white; }
            .no-print { display: none !important; }
            * { font-family: 'Arial', sans-serif; }
            .MuiPaper-root { box-shadow: none !important; border: 1px solid #aaa !important; margin-bottom: 20px !important; page-break-inside: auto; }
            tr { page-break-inside: avoid; }
            .exam-grid-print { display: grid !important; grid-template-columns: repeat(3, 1fr) !important; gap: 8px !important; }
            .exam-card-print { border: 1px solid #bbb !important; background-color: transparent !important; border-radius: 4px; padding: 8px; page-break-inside: avoid; height: auto !important; }
          }
      `}</style>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mb: 2 }} className="no-print">
         <Button variant="contained" color="primary" startIcon={<PrintIcon />} onClick={generatePlanningGridPDF}>
            Imprimer le Planning (PDF)
         </Button>
         <Button
           variant="outlined"
           color="success"
           startIcon={<ExcelIcon />}
           onClick={generatePlanningExcel}
           disabled={exams.length === 0}
         >
            Exporter Excel
         </Button>
      </Box>

      <Box id="daily-planning-container">
        <Typography variant="h4" align="center" sx={{ mb: 2, display: 'none', '@media print': { display: 'block' }, fontWeight: 'bold' }}>
            Planning des Examens
        </Typography>

        {uniqueDates.length === 0 ? (
          <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 5 }}>
            Aucun examen planifié.
          </Typography>
        ) : (
          uniqueDates.map((dateStr) => {
             const dayExams = getExamsForDate(dateStr);
             const dateObj = new Date(dateStr + 'T00:00:00');
             
             // Group by START TIME
             const timeSlots = {};
             dayExams.forEach(exam => {
                 const key = exam.start_time.substring(0,5); 
                 if(!timeSlots[key]) timeSlots[key] = [];
                 timeSlots[key].push(exam);
             });

             return (
               <Paper key={dateStr} elevation={3} sx={{ mb: 4, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
                 <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 1.5, display: 'flex', alignItems: 'center', gap: 2, '@media print': { bgcolor: '#1976d2 !important', color: 'white !important', padding: '5px' } }}>
                    <CalendarIcon />
                    <Typography variant="h6" sx={{ textTransform: 'capitalize', fontWeight: 'bold', color: 'inherit', flex: 1 }}>
                      {dateObj.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PdfIcon />}
                      className="no-print"
                      onClick={() => generateDayEnvelopes(dateStr, dayExams)}
                      sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.6)', whiteSpace: 'nowrap', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.15)' } }}
                    >
                      Enveloppes PDF
                    </Button>
                 </Box>

                 <Table size="small" sx={{ width: '100%' }}>
                    <TableBody>
                        {Object.entries(timeSlots).map(([startTime, examsInSlot]) => {
                            const mergedExams = mergeExamsByModuleGroup(examsInSlot);

                            return (
                                <TableRow key={startTime} sx={{ verticalAlign: 'top' }}>
                                    <TableCell sx={{ width: '110px', bgcolor: '#f8f9fa', verticalAlign: 'middle', textAlign: 'center', borderRight: '1px solid #e0e0e0', '@media print': { width: '90px', bgcolor: '#eee !important' } }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <AccessTimeIcon color="action" fontSize="small" sx={{ mb: 0.5 }} />
                                            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.2rem' }}>{startTime}</Typography>
                                        </Box>
                                    </TableCell>
                                    
                                    <TableCell sx={{ p: 2 }}>
                                        <Box className="exam-grid-print">
                                            <Grid container spacing={2} sx={{ '@media print': { display: 'none' } }}>
                                                {mergedExams.map((exam, i) => {
                                                    const ids = exam.ids || [exam.id];
                                                    const isSelected = ids.every(id => selectedExams?.has(id));
                                                    const isPartial = !isSelected && ids.some(id => selectedExams?.has(id));
                                                    return (
                                                    <Grid item xs={12} md={6} lg={4} xl={3} key={i}>
                                                        <Card
                                                            variant="outlined"
                                                            sx={{
                                                                bgcolor: isSelected ? '#e3f2fd' : getExamColor(exam.module_code),
                                                                borderColor: isSelected ? '#1976d2' : 'rgba(0,0,0,0.12)',
                                                                borderWidth: isSelected ? 2 : 1,
                                                                cursor: 'pointer',
                                                                height: 'auto', minHeight: '100%',
                                                                transition: 'transform 0.1s',
                                                                '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
                                                                position: 'relative'
                                                            }}
                                                            onClick={() => onSelectExam(exam)}
                                                        >
                                                            {/* Selection checkbox — left */}
                                                            {!readOnly && (
                                                              <Box
                                                                  sx={{ position: 'absolute', top: 4, left: 4, zIndex: 2 }}
                                                                  onClick={e => { e.stopPropagation(); onToggleSelect?.(ids); }}
                                                              >
                                                                  <Checkbox
                                                                      size="small"
                                                                      checked={isSelected}
                                                                      indeterminate={isPartial}
                                                                      sx={{ p: 0.3, bgcolor: 'rgba(255,255,255,0.85)', borderRadius: 1 }}
                                                                  />
                                                              </Box>
                                                            )}
                                                            {/* Edit button — top right */}
                                                            {!readOnly && (
                                                              <Tooltip title="Modifier">
                                                                <IconButton
                                                                  size="small"
                                                                  sx={{ position: 'absolute', top: 4, right: 4, zIndex: 2, p: 0.3, bgcolor: 'rgba(255,255,255,0.85)', borderRadius: 1 }}
                                                                  onClick={e => { e.stopPropagation(); onEditExam?.(exam); }}
                                                                >
                                                                  <EditIcon sx={{ fontSize: '0.9rem' }} />
                                                                </IconButton>
                                                              </Tooltip>
                                                            )}
                                                            <CardContent sx={{ p: '12px !important', pl: '36px !important', pr: '36px !important' }}>
                                                                <ExamCardContent exam={exam} />
                                                            </CardContent>
                                                        </Card>
                                                    </Grid>
                                                    );
                                                })}
                                            </Grid>
                                            <Box sx={{ display: 'none', '@media print': { display: 'contents' } }}>
                                                {mergedExams.map((exam, i) => (
                                                    <Box key={i} className="exam-card-print" sx={{ bgcolor: getExamColor(exam.module_code) }}>
                                                        <ExamCardContent exam={exam} />
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                 </Table>
               </Paper>
             );
          })
        )}
      </Box>
    </Box>
  );
};

// --- MAIN EXAM PLANNING COMPONENT ---
const ExamPlanning = () => {
  const [exams, setExams] = useState([]);
  const [rawStats, setRawStats] = useState([]);
  const [sessionLabel, setSessionLabel] = useState('الدورة: الخريفية الـــعـــاديــــة');
  const [sessionLabelDraft, setSessionLabelDraft] = useState('');
  const [locations, setLocations] = useState([]);
  const [dbProfessors, setDbProfessors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [planningLoading, setPlanningLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [viewMode, setViewMode] = useState('calendar'); 
  const [conflictCount, setConflictCount] = useState(0);

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const openNotif = Boolean(anchorEl);

  // Planning State
  const [selectedModules, setSelectedModules] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [allGroupStudents, setAllGroupStudents] = useState([]);
  const [planningSessions, setPlanningSessions] = useState([]);
  const [commonDate, setCommonDate] = useState('');
  const [commonStartTime, setCommonStartTime] = useState('');
  const [commonEndTime, setCommonEndTime] = useState('');

  // Range Distribution State
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);

  // Dialogs
  const [locationDialog, setLocationDialog] = useState(false);
  const [studentDialog, setStudentDialog] = useState({ 
      open: false, 
      students: [], 
      byLocation: [], 
      title: '', 
      loading: false 
  });
  const [conflictDialog, setConflictDialog] = useState({ open: false, conflicts: [], loading: false });
  const [newLoc, setNewLoc] = useState({ name: '', capacity: '', type: 'AMPHI' });

  // Edit dialog state
  const [editDialog, setEditDialog] = useState({ open: false, exam: null });
  const [editForm, setEditForm] = useState({ exam_date: '', start_time: '', end_time: '', locations: [] });

  // Surveillant / Professeur State
  const [currentExam, setCurrentExam] = useState(null);
  const [surveillantName, setSurveillantName] = useState('');

  // Selection state
  const [selectedExams, setSelectedExams] = useState(new Set());

  const toggleSelectExam = (ids) => {
    setSelectedExams(prev => {
      const next = new Set(prev);
      const allSelected = ids.every(id => next.has(id));
      ids.forEach(id => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const selectAll = () => setSelectedExams(new Set(exams.map(e => e.id)));
  const clearSelection = () => setSelectedExams(new Set());

  const handleBulkDelete = async () => {
    if (selectedExams.size === 0) return;
    if (!window.confirm(`Supprimer ${selectedExams.size} examen(s) sélectionné(s) ? Cette action est irréversible.`)) return;
    setLoading(true);
    try {
      await Promise.all([...selectedExams].map(id => adminAPI.deleteExam(id)));
      clearSelection();
      await loadExamsForSession(currentSessionId);
      setSuccess(`${selectedExams.size} examen(s) supprimé(s).`);
    } catch (err) {
      setError("Erreur lors de la suppression.");
    } finally {
      setLoading(false);
    }
  };

  // Session state
  const [sessions, setSessions]               = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [sessionDialog, setSessionDialog]     = useState(false);
  const [newSession, setNewSession]           = useState({ name: '', session_type: 'NORMALE', academic_year: new Date().getFullYear().toString(), source_session_id: '' });
  const [rattrapageSituations, setRattrapageSituations] = useState([]);
  const [rattSyncBusy, setRattSyncBusy]       = useState(false);
  const [snapshotDialog, setSnapshotDialog]   = useState(false);
  const [snapshotName, setSnapshotName]       = useState('');

  // Sync progress dialog
  const [syncProgressDialog, setSyncProgressDialog] = useState(false);
  const [syncProgressStep, setSyncProgressStep]     = useState(0);
  const [syncProgressMsg, setSyncProgressMsg]       = useState('');
  const [syncProgressDone, setSyncProgressDone]     = useState(false);
  const [syncProgressError, setSyncProgressError]   = useState(null);

  // Rattrapage generation dialog
  const [rattrapageDialog, setRattrapageDialog]   = useState(false);
  const [rattrapageBusy, setRattrapageBusy]       = useState(false);
  const [rattrapageMessage, setRattrapageMessage] = useState('');
  const [rattrapageResult, setRattrapageResult]   = useState(null);
  const [rattrapageError, setRattrapageError]     = useState(null);
  const [rattrapageSource, setRattrapageSource]   = useState(null);
  const [rattrapageStartDate, setRattrapageStartDate] = useState('');

  const SESSION_TYPES = ['NORMALE', 'RATTRAPAGE', 'PRINTEMPS', 'AUTOMNE', 'SPECIALE'];
  const SESSION_COLORS = { NORMALE: '#1976d2', RATTRAPAGE: '#e65100', PRINTEMPS: '#2e7d32', AUTOMNE: '#6a1b9a', SPECIALE: '#c62828', ARCHIVED: '#757575' };

  // Derived — is the currently viewed session archived (read-only)?
  const currentSession = sessions.find(s => s.id === currentSessionId);
  const isArchived = currentSession?.status === 'ARCHIVED';
  const isRattrapageSession = currentSession?.session_type === 'RATTRAPAGE';

  const handleArchiveAndNew = () => {
    // Pre-fill archive name with current session name or a default
    const defaultName = currentSession?.name
      ? `${currentSession.name} — Archivé`
      : `Planning ${new Date().toLocaleDateString('fr-FR')}`;
    setSnapshotName(defaultName);
    setSnapshotDialog(true);
  };

  const handleConfirmSnapshot = async () => {
    if (!snapshotName.trim()) return;
    try {
      const result = await adminAPI.snapshotExamSession({
        name: snapshotName.trim(),
        session_type: currentSession?.session_type || 'NORMALE',
        academic_year: currentSession?.academic_year || new Date().getFullYear().toString(),
      });
      setSnapshotDialog(false);
      setSnapshotName('');
      setCurrentSessionId(null);
      await loadData();
      setExams([]);
      setSuccess(`✅ ${result.moved_count} examens archivés dans "${result.name}". Workspace vide — créez votre nouveau planning.`);
      setSessionDialog(true);
    } catch (err) {
      setError("Erreur lors de l'archivage du planning.");
    }
  };

  // --- FETCH DATA ---
  const loadData = async () => {
    setLoading(true);
    try {
      const [sessionsData, locsData, profsData, settingsData] = await Promise.all([
        adminAPI.getExamSessions(),
        adminAPI.getLocations(),
        adminAPI.getProfessors(),
        adminAPI.getSettings(),
      ]);
      setDbProfessors(profsData);
      const lbl = settingsData?.flat?.find(s => s.key === 'exam.session_label')?.value;
      if (lbl) { setSessionLabel(lbl); setSessionLabelDraft(lbl); }
      else setSessionLabelDraft(sessionLabel);
      setSessions(sessionsData);

      // Determine active session
      const current = sessionsData.find(s => s.is_current) || sessionsData.find(s => s.status === 'ACTIVE') || sessionsData[0] || null;
      const activeId = current?.id || null;
      setCurrentSessionId(prev => prev || activeId);

      // Load exams for the current session (or all if none exists yet)
      const [examsData, statsData, conflicts] = await Promise.all([
        adminAPI.getExams(activeId),
        adminAPI.getDetailedStats(),
        adminAPI.getExamConflictsCount(activeId),
      ]);
      setExams(examsData);
      setRawStats(statsData);
      setConflictCount(conflicts);
      setLocations(locsData.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })));
    } catch (err) {
      console.error(err);
      setError("Erreur lors du chargement des données.");
    } finally {
      setLoading(false);
    }
  };

  const loadExamsForSession = async (sessionId) => {
    setLoading(true);
    try {
      const [examsData, conflicts] = await Promise.all([
        adminAPI.getExams(sessionId),
        adminAPI.getExamConflictsCount(sessionId),
      ]);
      setExams(examsData);
      setConflictCount(conflicts);
    } catch (err) {
      setError("Erreur chargement examens.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSession = (sessionId) => {
    setCurrentSessionId(sessionId);
    clearSelection();
    loadExamsForSession(sessionId);
  };

  const handleCreateSession = async () => {
    if (!newSession.name.trim()) return;
    try {
      const created = await adminAPI.createExamSession(newSession);
      setSessionDialog(false);
      setNewSession({ name: '', session_type: 'NORMALE', academic_year: new Date().getFullYear().toString(), source_session_id: '' });
      await loadData();
      setCurrentSessionId(created.id);
      loadExamsForSession(created.id);
      setSuccess(`Session "${created.name}" créée avec succès.`);
    } catch (err) {
      setError("Erreur lors de la création de la session.");
    }
  };

  const handleSyncRattrapageList = async () => {
    if (!currentSessionId) return;
    setRattSyncBusy(true);
    setError(null); setSuccess(null);
    try {
      const stats = await adminAPI.syncRattrapageList(currentSessionId);
      const situations = await adminAPI.getRattrapageSituations(currentSessionId);
      setRattrapageSituations(situations);
      setSuccess(`Liste rattrapage synchronisée : ${stats.modulesProcessed} module(s), ${stats.totalConcernedStudents} étudiant(s) concerné(s).`);
    } catch (err) {
      const data = err.response?.data || {};
      const parts = [data.error || err.message || String(err)];
      if (data.detail) parts.push(`Detail: ${data.detail}`);
      if (data.hint)   parts.push(`Hint: ${data.hint}`);
      setError(`Erreur sync rattrapage : ${parts.join(' — ')}`);
    } finally {
      setRattSyncBusy(false);
    }
  };

  const handleArchiveSession = async (sessionId) => {
    const session = sessions.find(s => s.id === sessionId);
    if (!window.confirm(`Archiver la session "${session?.name}" ? Elle ne sera plus modifiable.`)) return;
    try {
      await adminAPI.archiveExamSession(sessionId);
      await loadData();
      setSuccess("Session archivée.");
    } catch (err) {
      setError("Erreur lors de l'archivage.");
    }
  };

  const handleActivateSession = async (sessionId) => {
    try {
      await adminAPI.activateExamSession(sessionId);
      await loadData();
      setCurrentSessionId(sessionId);
      loadExamsForSession(sessionId);
    } catch (err) {
      setError("Erreur lors de l'activation.");
    }
  };

  const handleGenerateRattrapage = (session) => {
    setRattrapageSource(session);
    setRattrapageError(null);
    setRattrapageResult(null);
    setRattrapageMessage('');
    setRattrapageStartDate('');
    setRattrapageDialog(true);
  };

  const handleConfirmGenerateRattrapage = async () => {
    if (!rattrapageSource || !rattrapageStartDate) return;
    setRattrapageBusy(true);
    setRattrapageError(null);
    setRattrapageResult(null);
    setRattrapageMessage('Démarrage...');
    try {
      const { jobId } = await adminAPI.generateRattrapageSession(rattrapageSource.id, undefined, rattrapageStartDate);

      await new Promise((resolve, reject) => {
        const interval = setInterval(async () => {
          try {
            const job = await adminAPI.getRattrapageGenerationStatus(jobId);
            setRattrapageMessage(job.stepMessage || '');
            if (job.status === 'done') {
              clearInterval(interval);
              setRattrapageResult(job.result);
              resolve();
            } else if (job.status === 'error') {
              clearInterval(interval);
              reject(new Error(job.stepMessage || 'Erreur inconnue'));
            }
          } catch (e) {
            clearInterval(interval);
            reject(e);
          }
        }, 4000);
      });

      await loadData();
    } catch (err) {
      setRattrapageError(err.message || 'Erreur lors de la génération.');
    } finally {
      setRattrapageBusy(false);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    const session = sessions.find(s => s.id === sessionId);
    const examCount = parseInt(session?.exam_count) || 0;
    const confirmMsg = examCount > 0
      ? `Supprimer définitivement la session "${session?.name}" ? ${examCount} examen(s) et toutes les affectations d'étudiants associées seront supprimés. Cette action est irréversible.`
      : `Supprimer la session "${session?.name}" ?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await adminAPI.deleteExamSession(sessionId, examCount > 0);
      await loadData();
      setSuccess("Session supprimée.");
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors de la suppression.");
    }
  };

  const handleSaveSessionLabel = async () => {
    if (!sessionLabelDraft.trim() || sessionLabelDraft === sessionLabel) return;
    try {
      await adminAPI.updateSettings([{ key: 'exam.session_label', value: sessionLabelDraft.trim() }]);
      setSessionLabel(sessionLabelDraft.trim());
      setSuccess('Intitulé de la session mis à jour.');
    } catch { setError("Erreur lors de la sauvegarde."); }
  };

  const fetchNotifications = async () => {
    try {
      const data = await adminAPI.getNotifications();
      setNotifications(data.notifications);
      setUnreadCount(data.unread_count);
    } catch (err) {
      console.error("Failed to load notifications", err);
    }
  };

  useEffect(() => {
    loadData();
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // When viewing a RATTRAPAGE session, "Nouvelle Planification" sources modules/students
  // from the rattrapage candidate list instead of the normal pedagogical situation.
  useEffect(() => {
    const session = sessions.find(s => s.id === currentSessionId);
    if (session?.session_type === 'RATTRAPAGE') {
      adminAPI.getRattrapageSituations(currentSessionId).then(setRattrapageSituations).catch(() => setRattrapageSituations([]));
    } else {
      setRattrapageSituations([]);
    }
  }, [currentSessionId, sessions]);

  const handleNotificationClick = (event) => setAnchorEl(event.currentTarget);
  const handleNotificationClose = () => {
    setAnchorEl(null);
    if (unreadCount > 0) {
      adminAPI.markNotificationsRead();
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    }
  };

  const handleRefreshParticipants = async () => {
    if (!window.confirm("Reconstruire les groupes depuis la situation pédagogique et redistribuer les étudiants ?")) return;

    // Open progress dialog
    setSyncProgressDialog(true);
    setSyncProgressDone(false);
    setSyncProgressError(null);
    setSyncProgressStep(0);
    setSyncProgressMsg('Démarrage...');

    try {
      const { jobId } = await adminAPI.refreshExamParticipants();

      // Poll until done
      const poll = async () => {
        try {
          const job = await adminAPI.getRefreshParticipantsStatus(jobId);
          setSyncProgressStep(job.step || 0);
          setSyncProgressMsg(job.stepMessage || '');

          if (job.status === 'done') {
            setSyncProgressDone(true);
            await loadData();
          } else if (job.status === 'error') {
            setSyncProgressError(job.stepMessage || 'Erreur inconnue.');
          } else {
            setTimeout(poll, 2000);
          }
        } catch (_) {
          setTimeout(poll, 3000);
        }
      };
      setTimeout(poll, 2000);

    } catch (err) {
      setSyncProgressError('Impossible de démarrer la synchronisation.');
    }
  };
  
  const globalStats = useMemo(() => {
    const totalSessions = exams.length;
    const totalAssigned = exams.reduce((sum, e) => sum + (e.assigned_count || 0), 0);
    const uniqueModules = new Set(exams.map(e => e.module_code)).size;
    const uniqueDays = new Set(exams.map(e => {
        const d = new Date(e.exam_date.split('T')[0] + 'T00:00:00');
        return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    })).size;
    return { totalSessions, totalAssigned, uniqueModules, uniqueDays };
  }, [exams]);

  const handleShowConflicts = async () => {
    if (conflictCount === 0) return;
    setConflictDialog({ open: true, conflicts: [], loading: true });
    try {
      const data = await adminAPI.getExamConflictsDetails(currentSessionId);
      setConflictDialog({ open: true, conflicts: data, loading: false });
    } catch (err) {
      console.error(err);
      setConflictDialog({ open: false, conflicts: [], loading: false });
      setError("Erreur chargement détails conflits");
    }
  };

  const handleExport = async () => {
    try {
        setLoading(true);
        await adminAPI.exportExamAssignments(currentSessionId);
        setSuccess("Exportation réussie !");
    } catch (err) {
        console.error(err);
        setError("Erreur lors de l'exportation.");
    } finally {
        setLoading(false);
    }
  };

  const handleSelectData = async (modules, groups) => {
    setSelectedModules(modules);
    setSelectedGroups(groups);
    setPlanningSessions([]);

    if (modules && modules.length > 0) {
      setPlanningLoading(true);
      try {
        let uniqueStudents;
        const getStudents = isRattrapageSession
          ? (modCode, groupNames) => adminAPI.getRattrapageStudents(currentSessionId, modCode, groupNames)
          : (modCode, groupNames) => adminAPI.getGroupStudents(modCode, groupNames);

        const requests = [];
        if (groups.length === 0) {
            const moduleCodes = modules.map(m => m.cod_elp).join(',');
            requests.push(getStudents(moduleCodes, 'Tous'));
        } else {
            const moduleMap = new Map();
            groups.forEach(g => {
                if (!moduleMap.has(g.cod_elp)) moduleMap.set(g.cod_elp, []);
                moduleMap.get(g.cod_elp).push(g.group_name);
            });
            for (const [modCode, groupNames] of moduleMap.entries()) {
                if (groupNames.length > 0) {
                    requests.push(getStudents(modCode, groupNames.join(',')));
                }
            }
        }
        if (requests.length === 0) {
            setAllGroupStudents([]);
            setLoading(false);
            return;
        }
        const results = await Promise.all(requests);
        const allStudents = results.flat();
        uniqueStudents = Array.from(new Map(allStudents.map(s => [s.cod_etu, s])).values());
        uniqueStudents.sort((a, b) => (a.lib_nom_pat_ind || '').localeCompare(b.lib_nom_pat_ind || ''));
        setAllGroupStudents(uniqueStudents);
        if (uniqueStudents.length === 0) {
          setError(isRattrapageSession
            ? "Aucun étudiant trouvé pour ce module. Synchronisez la liste rattrapage."
            : "Aucun étudiant trouvé pour ce module / groupe. Vérifiez que la répartition pédagogique a bien été synchronisée.");
        }
      } catch (err) {
        console.error(err);
        setError(`Erreur récupération étudiants: ${err.response?.data?.details || err.message}`);
      } finally {
        setPlanningLoading(false);
      }
    } else {
      setAllGroupStudents([]);
    }
  };

  const handleAutoDistribute = () => {
    if (!rangeStart || !rangeEnd) return;
    if (allGroupStudents.length === 0) { setError("Aucun étudiant à répartir."); return; }
    const idx1 = locations.findIndex(l => l.id === rangeStart.id);
    const idx2 = locations.findIndex(l => l.id === rangeEnd.id);
    if (idx1 === -1 || idx2 === -1) return;
    const start = Math.min(idx1, idx2);
    const end = Math.max(idx1, idx2);
    const locationSubset = locations.slice(start, end + 1);
    
    const totalCapacity = locationSubset.reduce((sum, loc) => sum + (parseInt(loc.capacity) || 0), 0);
    const ratio = Math.min(allGroupStudents.length / totalCapacity, 1);
    const newSessions = [];
    let assignedSoFar = 0;

    locationSubset.forEach((loc, index) => {
        const cap = parseInt(loc.capacity) || 0;
        let count = Math.floor(cap * ratio); 
        assignedSoFar += count;
        newSessions.push({ id: Date.now() + index, location: loc, count: count, professor: '' });
    });

    let remainder = allGroupStudents.length - assignedSoFar;
    if (remainder > 0) {
        for (const session of newSessions) {
            if (remainder <= 0) break;
            const cap = parseInt(session.location.capacity) || 0;
            if (session.count < cap) { session.count++; remainder--; }
        }
    }
    setPlanningSessions(newSessions.filter(s => s.count > 0));
    setSuccess(`Répartition automatique effectuée.`);
  };

  const handleAddSession = () => {
    setPlanningSessions([...planningSessions, { id: Date.now(), location: null, count: 0, professor: '' }]);
  };

  const handleUpdateSession = (id, field, value) => {
    setPlanningSessions(prev => prev.map(session => {
      if (session.id !== id) return session;
      const updated = { ...session, [field]: value };
      if (field === 'location' && value) {
        const allocatedOthers = prev.filter(s => s.id !== id).reduce((sum, s) => sum + (parseInt(s.count) || 0), 0);
        const remainingTotal = allGroupStudents.length - allocatedOthers;
        updated.count = Math.min(value.capacity, Math.max(0, remainingTotal));
      }
      return updated;
    }));
  };

  const handleRemoveSession = (id) => {
    setPlanningSessions(prev => prev.filter(s => s.id !== id));
  };

  const handleSubmitPlan = async () => {
    setError(null); setSuccess(null);
    if (selectedModules.length === 0 || !commonDate || !commonStartTime || !commonEndTime || planningSessions.length === 0) {
      setError("Veuillez remplir tous les champs communs et ajouter au moins une session."); return;
    }
    if (commonStartTime >= commonEndTime) { setError("L'heure de fin doit être après l'heure de début."); return; }
    
    setLoading(true);
    try {
      let startIndex = 0;
      const mergedCode = selectedModules.map(m => m.cod_elp).join(' + ');
      const mergedName = selectedModules.map(m => m.lib_elp).join(' & ');
      const mergedGroups = selectedGroups.length > 0 ? selectedGroups.map(g => `${g.group_name}(${g.cod_elp})`).join(' + ') : 'Tous';
      for (const session of planningSessions) {
        if (!session.location || !session.count) continue;
        const count = parseInt(session.count);
        const sessionStudents = allGroupStudents.slice(startIndex, startIndex + count);
        const studentIds = sessionStudents.map(s => s.cod_etu);
        const payload = {
          module_code: mergedCode, module_name: mergedName, group_name: mergedGroups, exam_date: commonDate,
          start_time: commonStartTime, end_time: commonEndTime, location: session.location.name,
          professor_name: session.professor, student_ids: studentIds,
          // Always attach to the session being viewed/planned, never the globally
          // "current" one — otherwise planning into an inactive session (e.g. a
          // RATTRAPAGE session kept inactive on purpose) would silently fall back
          // to whichever session is_current, planning into the wrong place.
          session_id: currentSessionId,
        };
        await adminAPI.createExam(payload);
        startIndex += count;
      }
      setSuccess(`Planification réussie pour ${startIndex} étudiants !`);
      setSelectedModules([]); 
      setSelectedGroups([]); 
      setAllGroupStudents([]); 
      setPlanningSessions([]);
      loadData();
    } catch (err) {
      console.error(err); setError("Erreur lors de la planification.");
    } finally {
      setLoading(false);
    }
  };

  // All distinct modules from student_group_assignments (= what appears in Total Affectations export)
  // — or, for a RATTRAPAGE session, the synced rattrapage candidate list instead.
  const allPedagoModules = useMemo(() => {
    const source = isRattrapageSession ? rattrapageSituations : rawStats;
    const map = new Map();
    source.forEach(item => {
      const key = item.cod_elp;
      if (key && !map.has(key)) {
        map.set(key, { cod_elp: key, lib_elp: item.lib_elp || key });
      }
    });
    return Array.from(map.values()).sort((a, b) => (a.lib_elp || '').localeCompare(b.lib_elp || ''));
  }, [rawStats, isRattrapageSession, rattrapageSituations]);

  // Groups dropdown: filter by exact cod_elp — never use the pattern. For RATTRAPAGE
  // sessions, group_name comes from rattrapage_situations (the original pedagogical
  // group recorded per student when the list was synced) instead of rawStats.
  const availableGroups = useMemo(() => {
    if (selectedModules.length === 0) return [];
    const source = isRattrapageSession ? rattrapageSituations : rawStats;
    const seen = new Map();
    selectedModules.forEach(mod => {
      source
        .filter(item => item.cod_elp === mod.cod_elp && item.group_name)
        .forEach(item => {
          const key = `${mod.cod_elp}-${item.group_name}`;
          if (!seen.has(key)) {
            seen.set(key, {
              key,
              group_name: item.group_name,
              cod_elp:    mod.cod_elp,
              lib_elp:    mod.lib_elp,
              label:      `${item.group_name} (${mod.cod_elp})`
            });
          }
        });
    });
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [rawStats, selectedModules, isRattrapageSession, rattrapageSituations]);

  const allocatedTotal = planningSessions.reduce((sum, s) => sum + (parseInt(s.count) || 0), 0);
  const remainingStudents = allGroupStudents.length - allocatedTotal;

  // --- EDIT EXAM ---
  const handleOpenEdit = (exam) => {
    const ids = exam.ids || [exam.id];
    const locs = ids.map((id, i) => ({
      id,
      location: exam.all_locations ? (exam.all_locations[i] || '') : (exam.location || '')
    }));
    setEditForm({
      exam_date:   exam.exam_date ? exam.exam_date.split('T')[0] : '',
      start_time:  exam.start_time?.substring(0, 5) || '',
      end_time:    exam.end_time?.substring(0, 5) || '',
      locations:   locs
    });
    setEditDialog({ open: true, exam });
  };

  const handleSaveEdit = async () => {
    if (editForm.start_time >= editForm.end_time) {
      setError("L'heure de fin doit être après l'heure de début.");
      return;
    }
    setLoading(true);
    try {
      await Promise.all(editForm.locations.map(({ id, location }) =>
        adminAPI.updateExam(id, {
          exam_date:  editForm.exam_date,
          start_time: editForm.start_time,
          end_time:   editForm.end_time,
          location
        })
      ));
      setEditDialog({ open: false, exam: null });
      setSuccess('Planification mise à jour avec succès.');
      await loadExamsForSession(currentSessionId);
    } catch {
      setError('Erreur lors de la mise à jour.');
    } finally {
      setLoading(false);
    }
  };

  // --- HANDLE EXAM CLICK (SEPARATE LISTS PER ROOM) ---
  const handleExamClick = async (exam) => {
    setCurrentExam(exam);
    setSurveillantName(exam.professor_name || ''); 
    
    const locs = exam.all_locations ? [...exam.all_locations].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })) : [exam.location];
    const locString = locs.join(' + ');

    setStudentDialog({ 
        open: true, 
        students: [], 
        byLocation: [], 
        loading: true, 
        title: `${exam.module_name} (${locString})` 
    });
    
    try {
        const ids = exam.ids || [exam.id];
        const promises = ids.map(id => adminAPI.getExamStudents(id));
        const results = await Promise.all(promises);
        
        // 1. All Students (for Display)
        const allStudentsMap = new Map();
        results.flat().forEach(s => {
            if (!allStudentsMap.has(s.cod_etu)) allStudentsMap.set(s.cod_etu, s);
        });
        const sortedAll = Array.from(allStudentsMap.values()).sort((a, b) =>
            (a.lib_nom_pat_ind || '').localeCompare(b.lib_nom_pat_ind || '') ||
            (a.lib_pr1_ind     || '').localeCompare(b.lib_pr1_ind     || '')
        );

        // 2. Per-Location Data (for PDF)
        const byLocationData = [];
        ids.forEach((id, index) => {
            const locName = exam.all_locations ? exam.all_locations[index] : exam.location;
            const studentsForLoc = results[index].sort((a, b) =>
              (a.lib_nom_pat_ind || '').localeCompare(b.lib_nom_pat_ind || '') ||
              (a.lib_pr1_ind     || '').localeCompare(b.lib_pr1_ind     || '')
            );
            
            byLocationData.push({
                location: locName,
                students: studentsForLoc
            });
        });

        byLocationData.sort((a, b) => a.location.localeCompare(b.location, undefined, { numeric: true }));

        setStudentDialog(p => ({ 
            ...p, 
            students: sortedAll, 
            byLocation: byLocationData,
            loading: false 
        }));

    } catch (err) {
        console.error("Error fetching students", err);
        setStudentDialog(p => ({ ...p, loading: false }));
    }
  };

  const handleSelectExam = (exam) => {
    handleExamClick(exam);
  };

 const handleSaveSurveillant = async () => {
    if (!currentExam) return;
    try {
      const idsToUpdate = currentExam.ids || [currentExam.id];
      await Promise.all(idsToUpdate.map(id => 
        adminAPI.updateExam(id, { professor_name: surveillantName })
      ));
      
      setExams(prevExams => prevExams.map(e => 
        idsToUpdate.includes(e.id) ? { ...e, professor_name: surveillantName } : e
      ));

      setCurrentExam(prev => ({ ...prev, professor_name: surveillantName }));
      setSuccess("Surveillant mis à jour avec succès !");
    } catch (err) {
      console.error(err);
      setError("Erreur lors de la mise à jour du surveillant.");
    }
  };

  // --- PDF GENERATION WITH TEMPLATE & ARABIC FONT ---
  const handleGeneratePDF = async () => {
    if (!currentExam || !studentDialog.byLocation.length) return;
    setLoading(true);

    try {
        const doc = new jsPDF();
        
        // 1. FONTS & IMAGES
        const fontUrl = '/fonts/Amiri-Regular.ttf';
        const fontBytes = await fetch(fontUrl).then(res => res.arrayBuffer());
        doc.addFileToVFS('Amiri-Regular.ttf', arrayBufferToBase64(fontBytes));
        doc.addFont('Amiri-Regular.ttf', 'Amiri', 'normal');
        doc.setFont('Amiri'); // Set global font

        // Fetch Logos (Assuming in public folder)
        let logo1 = null, logo2 = null;
        try {
            const l1 = await fetch('/logo1.png').then(r => r.arrayBuffer());
            const l2 = await fetch('/logo2.png').then(r => r.arrayBuffer());
            logo1 = arrayBufferToBase64(l1);
            logo2 = arrayBufferToBase64(l2);
        } catch(e) { console.warn("Logos not found"); }

        const examDate = new Date(currentExam.exam_date.split('T')[0] + 'T00:00:00').toLocaleDateString('fr-FR');
        const startTime = currentExam.start_time.substring(0, 5);
        const endTime = currentExam.end_time.substring(0, 5);
        const groupString = currentExam.all_groups ? currentExam.all_groups.join(' + ') : currentExam.group_name;
        const moduleDisplayName = getCleanModuleName(currentExam);
        const profName = surveillantName || '......................................';

        studentDialog.byLocation.forEach((data, index) => {
            
            if (index > 0) doc.addPage();
            
            // --- DRAW HEADER FUNCTION ---
            const drawHeader = () => {
                // Logos
                if(logo1) doc.addImage(logo1, 'PNG', 10, 10, 25, 25);
                if(logo2) doc.addImage(logo2, 'PNG', 175, 10, 25, 25);
                
                // Center Text
                doc.setFontSize(12);
                doc.setTextColor(0);
                doc.text(formatAcademicYearLabel(currentSession?.academic_year), 105, 15, { align: 'center' });
                doc.text(sessionLabel, 105, 22, { align: 'center' });
                doc.text(`Date : ${examDate}`, 105, 29, { align: 'center' });
            };

            // === PAGE A: PV ===
            drawHeader();
            
            // Big Amphi Name
            doc.setFontSize(25);
            doc.setFont('Amiri', 'bold');
            doc.text(data.location, 105, 45, { align: 'center' });
            doc.setFont('Amiri', 'normal');

            // PV Title
            doc.setFontSize(22);
            doc.text("محـــــــضـــــر الــمـــــراقـــــبــــــة", 105, 60, { align: 'center' }); 
            
            // Table: Counts
            doc.autoTable({
                startY: 70,
                theme: 'grid',
                styles: { font: 'Amiri', halign: 'center', fontSize: 11, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
                headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'center' },
                head: [['عدد الطلبة الغائبين', 'عدد الطلبة الحاضرين', 'عدد الطلبة المسجلين', 'السداسي والمسلك']],
                body: [['', '', data.students.length.toString(), '......................']],
            });
            
            // Supervisors
            doc.setFontSize(12);
            doc.text("الأساتذة المشرفون على المراقبة", 190, 100, { align: 'right' });
            doc.line(14, 101, 196, 101);

            let yPos = 110;
            const dots = ".............................................";
            doc.text(`الأستاذ(ة) المسؤول(ة) : ${dots}              .................................`, 190, yPos, { align: 'right' });
            yPos += 10;
            doc.text(`الأستاذ(ة) : ${dots}                  .................................`, 190, yPos, { align: 'right' });
            yPos += 10;
            doc.text(`الأستاذ(ة) : ${dots}                  .................................`, 190, yPos, { align: 'right' });

            // Admin
            yPos += 20;
            doc.autoTable({
                startY: yPos,
                theme: 'grid',
                styles: { font: 'Amiri', halign: 'right', fontSize: 11, cellPadding: 5, lineColor: [0, 0, 0], lineWidth: 0.1 },
                head: [['ملاحظات', 'خاص بالإدارة']],
                body: [['\n\n...................................................................\n\n...................................................................', '']],
                columnStyles: { 0: { cellWidth: 140 }, 1: { cellWidth: 40, fontStyle: 'bold', valign: 'middle', halign: 'center' } }
            });

            // Footer Details (Right Aligned)
            const footerY = 230; 
            doc.setFontSize(13);
            doc.text(`الأستاذ(ة): ${profName}`, 190, footerY, { align: 'right' });
            doc.text(`المادة: ${moduleDisplayName}`, 190, footerY + 8, { align: 'right' });
            doc.text(`الفوج: ${groupString}`, 190, footerY + 16, { align: 'right' });
            doc.text(`التوقيت: ${startTime} - ${endTime}`, 190, footerY + 24, { align: 'right' });
            doc.text(`القاعة: ${data.location}`, 190, footerY + 32, { align: 'right' });

            // === PAGE B: LIST ===
            doc.addPage();
            drawHeader();
            
            // Big Amphi on List too
            doc.setFontSize(25);
            doc.setFont('Amiri', 'bold');
            doc.text(data.location, 105, 45, { align: 'center' });
            doc.setFont('Amiri', 'normal');

           
            
            let listY = 70;
            doc.setFontSize(11);
            doc.setTextColor(40);
            
            // Info Block
            doc.text(`Date : ${examDate}`, 14, listY);
            doc.text(`Heure : ${startTime} - ${endTime}`, 80, listY);
            listY += 6;
            doc.text(`Module : ${moduleDisplayName}`, 14, listY);
            listY += 6;
            doc.text(`Groupe : ${groupString}`, 14, listY);
            listY += 6;
            doc.text(`Professeur : ${profName}`, 14, listY);

            doc.autoTable({
              startY: listY + 10,
              head: [["N°", "CNE", "Nom", "Prénom", "Signature"]],
              body: data.students.map((s, i) => [i + 1, s.cod_etu, s.lib_nom_pat_ind, s.lib_pr1_ind, ""]),
              theme: 'grid',
              headStyles: { fillColor: [41, 128, 185], textColor: 255 },
              styles: { fontSize: 10, cellPadding: 3, font: 'Amiri' },
              columnStyles: { 0: { cellWidth: 15, halign: 'center' }, 1: { cellWidth: 30 }, 2: { cellWidth: 45 }, 3: { cellWidth: 45 }, 4: { cellWidth: 'auto' } }
            });

            const pageCount = doc.internal.getNumberOfPages();
            doc.setFontSize(8);
            doc.text(`Page ${pageCount} - ${data.location}`, 190, 285, { align: 'right' });
        });

        doc.save(`emargement_${currentExam.module_code}_${examDate}.pdf`);

    } catch (err) {
        console.error("Error generating PDF:", err);
        setError("Erreur lors de la génération du PDF (Police 'Amiri' manquante ?)");
    } finally {
        setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }} className="no-print">
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <EventIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" color="primary" fontWeight="600">Planning des Examens</Typography>
            <Typography variant="body2" color="text.secondary">Gestion des sessions, conflits & listes.</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <IconButton color="primary" onClick={handleNotificationClick}>
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={openNotif}
              onClose={handleNotificationClose}
              PaperProps={{ sx: { width: 400, maxHeight: 400 } }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              {notifications.length > 0 && notifications.map(n => <MenuItem key={n.id}>{n.message}</MenuItem>)}
            </Menu>

            <Button startIcon={<SyncIcon />} variant="outlined" color="secondary" onClick={handleRefreshParticipants}>
                Sync Listes
            </Button>

            {isRattrapageSession && (
              <Button
                startIcon={rattSyncBusy ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                variant="outlined"
                sx={{ color: '#e65100', borderColor: '#e65100', '&:hover': { borderColor: '#bf360c', bgcolor: 'rgba(230,81,0,0.06)' } }}
                onClick={handleSyncRattrapageList}
                disabled={rattSyncBusy}
              >
                {rattSyncBusy ? 'Sync...' : 'Sync Ratt List'}
              </Button>
            )}

            <Button startIcon={<DownloadIcon />} variant="contained" color="success" onClick={handleExport} disabled={loading}>
                {loading ? 'Export...' : 'Exporter (CSV)'}
            </Button>
            
            <Button startIcon={<SettingsIcon />} variant="outlined" onClick={() => setLocationDialog(true)}>
                Gérer les Locaux
            </Button>
        </Box>
      </Box>
      
      {/* ── SESSION BAR ── */}
      {/* ── Session label editor ── */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 2, bgcolor: '#f8f9fa', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }} className="no-print">
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
          الدورة (PDF) :
        </Typography>
        <TextField
          size="small"
          value={sessionLabelDraft}
          onChange={e => setSessionLabelDraft(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSaveSessionLabel()}
          sx={{ flex: 1 }}
          inputProps={{ style: { textAlign: 'right', direction: 'rtl', fontFamily: "'Amiri', Arial, sans-serif", fontSize: '1rem' } }}
          placeholder="الدورة: الخريفية الـــعـــاديــــة"
        />
        <Button
          size="small"
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSaveSessionLabel}
          disabled={!sessionLabelDraft.trim() || sessionLabelDraft === sessionLabel}
        >
          Sauvegarder
        </Button>
      </Paper>

      <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 3 }} className="no-print">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SessionIcon color="primary" />
            <Typography variant="subtitle1" fontWeight={700}>Sessions d'Examens</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={
              isArchived ? "Cette session est déjà archivée" :
              exams.length === 0 ? "Aucun examen à archiver" :
              `Archiver les ${exams.length} examens visibles et vider le workspace`
            }>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<ArchiveIcon />}
                  onClick={handleArchiveAndNew}
                  disabled={isArchived || exams.length === 0}
                  sx={{ borderRadius: 2 }}
                >
                  Archiver & Nouvelle Session
                </Button>
              </span>
            </Tooltip>
            <Button
              size="small"
              variant="contained"
              startIcon={<NewSessionIcon />}
              onClick={() => setSessionDialog(true)}
              sx={{ borderRadius: 2 }}
            >
              Nouvelle Session
            </Button>
          </Box>
        </Box>

        {sessions.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Aucune session créée. Créez votre première session pour commencer la planification.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {sessions.map(s => {
              const isActive = s.id === currentSessionId;
              const color = SESSION_COLORS[s.status === 'ARCHIVED' ? 'ARCHIVED' : s.session_type] || '#1976d2';
              return (
                <Box
                  key={s.id}
                  sx={{
                    border: `2px solid ${isActive ? color : '#e0e0e0'}`,
                    borderRadius: 2,
                    p: 1.5,
                    minWidth: 180,
                    cursor: 'pointer',
                    bgcolor: isActive ? `${color}12` : 'white',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: color, bgcolor: `${color}08` },
                    position: 'relative'
                  }}
                  onClick={() => handleSelectSession(s.id)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="body2" fontWeight={700} sx={{ color: isActive ? color : 'text.primary' }}>
                        {s.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {s.session_type} — {s.academic_year}
                      </Typography>
                      <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={s.status === 'ARCHIVED' ? 'Archivée' : s.is_current ? 'Active' : 'Inactive'}
                          size="small"
                          sx={{ height: 18, fontSize: '0.65rem', bgcolor: s.status === 'ARCHIVED' ? '#eee' : s.is_current ? `${color}20` : '#f5f5f5', color: s.status === 'ARCHIVED' ? '#757575' : s.is_current ? color : '#999' }}
                        />
                        <Chip label={`${s.exam_count} exam${s.exam_count !== 1 ? 's' : ''}`} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                      {s.status !== 'ARCHIVED' && !s.is_current && (
                        <Tooltip title="Activer cette session">
                          <IconButton size="small" onClick={e => { e.stopPropagation(); handleActivateSession(s.id); }}>
                            <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {s.session_type !== 'RATTRAPAGE' && s.status !== 'ARCHIVED' && parseInt(s.exam_count) > 0 && (
                        <Tooltip title="Générer une session de rattrapage">
                          <IconButton size="small" onClick={e => { e.stopPropagation(); handleGenerateRattrapage(s); }}>
                            <RattrapageIcon sx={{ fontSize: 16, color: '#e65100' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {s.status !== 'ARCHIVED' && (
                        <Tooltip title="Archiver">
                          <IconButton size="small" onClick={e => { e.stopPropagation(); handleArchiveSession(s.id); }}>
                            <ArchiveIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!s.is_current && (
                        <Tooltip title={parseInt(s.exam_count) > 0 ? 'Supprimer (avec ses examens)' : 'Supprimer'}>
                          <IconButton size="small" onClick={e => { e.stopPropagation(); handleDeleteSession(s.id); }}>
                            <DeleteIcon sx={{ fontSize: 16, color: 'error.main' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>

      {/* ── SNAPSHOT DIALOG ── */}
      <Dialog open={snapshotDialog} onClose={() => setSnapshotDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ArchiveIcon color="warning" /> Archiver le planning actuel
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: 1, borderRadius: 2 }}>
            Tous les examens actuellement visibles ({exams.length}) seront déplacés dans une archive consultable. Le workspace sera vide pour un nouveau planning.
          </Alert>
          <TextField
            fullWidth
            label="Nom de l'archive"
            placeholder="ex: Session Automne 2025 — Normale"
            value={snapshotName}
            onChange={e => setSnapshotName(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSnapshotDialog(false)}>Annuler</Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<ArchiveIcon />}
            onClick={handleConfirmSnapshot}
            disabled={!snapshotName.trim()}
          >
            Archiver & Vider le workspace
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── GENERATE RATTRAPAGE DIALOG ── */}
      <Dialog open={rattrapageDialog} onClose={() => !rattrapageBusy && setRattrapageDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RattrapageIcon sx={{ color: '#e65100' }} /> Générer une session de Rattrapage
        </DialogTitle>
        <DialogContent>
          {!rattrapageResult && (
            <Alert severity="info" sx={{ mb: 2, mt: 1, borderRadius: 2 }}>
              Une nouvelle session RATTRAPAGE sera créée à partir de « {rattrapageSource?.name} ». Les étudiants
              avec une note &lt; 10 ou absente seront regroupés par module (tous groupes pédagogiques confondus)
              et automatiquement affectés aux locaux les plus adaptés à leur nombre. La nouvelle session restera
              inactive — vous pourrez ensuite ajuster les dates/heures par examen et l'activer manuellement.
            </Alert>
          )}

          {!rattrapageResult && (
            <TextField
              fullWidth
              type="date"
              label="Premier jour du rattrapage"
              InputLabelProps={{ shrink: true }}
              value={rattrapageStartDate}
              onChange={e => setRattrapageStartDate(e.target.value)}
              disabled={rattrapageBusy}
              sx={{ mt: 1 }}
              helperText="Les examens seront répartis automatiquement sur plusieurs jours à partir de cette date (créneaux d'1h, 09h-17h, jours ouvrés) — ajustable ensuite par examen."
            />
          )}

          {rattrapageBusy && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">{rattrapageMessage}</Typography>
            </Box>
          )}

          {rattrapageError && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{rattrapageError}</Alert>}

          {rattrapageResult && (
            <Box sx={{ mt: 1 }}>
              <Alert severity="success" sx={{ borderRadius: 2, mb: 1.5 }}>
                Session « {rattrapageResult.session?.name} » créée : {rattrapageResult.modulesProcessed} module(s),
                {' '}{rattrapageResult.totalConcernedStudents} étudiant(s), {rattrapageResult.examsCreated} examen(s),
                {' '}répartis sur <strong>{rattrapageResult.daysUsed}</strong> jour(s)
                {rattrapageResult.sourceDays ? ` (session normale : ${rattrapageResult.sourceDays} jour(s))` : ''}.
              </Alert>
              {rattrapageResult.warnings?.length > 0 && (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  {rattrapageResult.warnings.map((w, i) => <div key={i}>{w}</div>)}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRattrapageDialog(false)} disabled={rattrapageBusy}>
            {rattrapageResult ? 'Fermer' : 'Annuler'}
          </Button>
          {!rattrapageResult && (
            <Button
              variant="contained"
              sx={{ bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' } }}
              startIcon={rattrapageBusy ? <CircularProgress size={16} color="inherit" /> : <RattrapageIcon />}
              onClick={handleConfirmGenerateRattrapage}
              disabled={rattrapageBusy || !rattrapageStartDate}
            >
              {rattrapageBusy ? 'Génération...' : 'Générer'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── NEW SESSION DIALOG ── */}
      <Dialog open={sessionDialog} onClose={() => setSessionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NewSessionIcon color="primary" /> Nouvelle Session d'Examens
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Nom de la session"
              placeholder="ex: Session Automne 2025 — Normale"
              value={newSession.name}
              onChange={e => setNewSession(p => ({ ...p, name: e.target.value }))}
            />
            <TextField
              select
              fullWidth
              label="Type de session"
              value={newSession.session_type}
              onChange={e => setNewSession(p => ({ ...p, session_type: e.target.value }))}
              SelectProps={{ native: true }}
            >
              {SESSION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </TextField>
            {newSession.session_type === 'RATTRAPAGE' && (
              <TextField
                select
                fullWidth
                label="Session source (normale)"
                helperText="Détermine de quels examens/notes la liste rattrapage sera calculée (bouton « Sync Ratt List »)."
                value={newSession.source_session_id}
                onChange={e => setNewSession(p => ({ ...p, source_session_id: e.target.value }))}
                SelectProps={{ native: true }}
              >
                <option value=""></option>
                {sessions.filter(s => s.session_type !== 'RATTRAPAGE').map(s => (
                  <option key={s.id} value={s.id}>{s.name} ({s.session_type} — {s.academic_year})</option>
                ))}
              </TextField>
            )}
            <TextField
              fullWidth
              label="Année universitaire"
              placeholder="2025"
              value={newSession.academic_year}
              onChange={e => setNewSession(p => ({ ...p, academic_year: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSessionDialog(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleCreateSession} disabled={!newSession.name.trim()}>
            Créer la Session
          </Button>
        </DialogActions>
      </Dialog>

      {/* GLOBAL STATS */}
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary', mt: 2 }} className="no-print">
        <AssignmentIcon color="action" /> Statistiques Globales
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }} className="no-print">
        <Grid item xs={12} sm={6} md={2.4}>
           <StatCard title="Total Sessions" value={globalStats.totalSessions} subtitle="Examens programmés" icon={<EventIcon />} color="info" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
           <StatCard title="Étudiants Planifiés" value={globalStats.totalAssigned} subtitle="Total convocations" icon={<PeopleIcon />} color="success" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
           <StatCard title="Modules Uniques" value={globalStats.uniqueModules} subtitle="Matières distinctes" icon={<ClassIcon />} color="warning" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
           <StatCard title="Jours d'Examens" value={globalStats.uniqueDays} subtitle="Jours occupés" icon={<TodayIcon />} color="primary" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
           <StatCard title="Conflits Horaire" value={conflictCount} subtitle="Étudiants en doublon" icon={<WarningIcon />} color={conflictCount > 0 ? "error" : "success"} loading={loading} onClick={handleShowConflicts} />
        </Grid>
      </Grid>
      
      <Divider sx={{ my: 3 }} className="no-print" />

      {/* ── Archived session banner ── */}
      {isArchived && (
        <Alert
          severity="info"
          icon={<ArchiveIcon />}
          sx={{ mb: 2, borderRadius: 2 }}
          className="no-print"
          action={
            <Button size="small" color="inherit" startIcon={<NewSessionIcon />} onClick={() => setSessionDialog(true)}>
              Nouvelle Session
            </Button>
          }
        >
          <strong>Planning archivé — lecture seule.</strong> Vous consultez l'historique de «&nbsp;{currentSession?.name}&nbsp;». Pour planifier de nouveaux examens, créez une nouvelle session.
        </Alert>
      )}

      {/* PLANIFICATION FORM */}
      <Box sx={{ display: selectedModules.length > 0 ? 'block' : 'none', mb: 3 }} className="no-print">
        {planningLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="h6" color="text.secondary">Chargement des étudiants...</Typography>
          </Box>
        ) : (
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Planification en cours...
            {allGroupStudents.length > 0 && (
              <Chip label={`${allGroupStudents.length} étudiants`} color="primary" size="small" icon={<PeopleIcon />} />
            )}
          </Typography>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)} className="no-print">{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)} className="no-print">{success}</Alert>}

      <Grid container spacing={3}>
        {/* FORM LEFT SIDE */}
        <Grid item xs={12} md={5} className="no-print">
          <Card sx={{ borderRadius: 2, boxShadow: 3, opacity: isArchived ? 0.5 : 1, pointerEvents: isArchived ? 'none' : 'auto' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AddIcon color="primary" /> Nouvelle Planification
                {isArchived && <Chip label="Session archivée" size="small" color="default" sx={{ ml: 1 }} />}
                {isRattrapageSession && <Chip label="Liste rattrapage" size="small" sx={{ ml: 1, bgcolor: '#e65100', color: 'white' }} />}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                {isRattrapageSession && (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    Les modules et groupes ci-dessous viennent de la liste rattrapage synchronisée (bouton « Sync Ratt List »), pas de la répartition pédagogique normale.
                  </Alert>
                )}
                <Autocomplete
                  multiple
                  options={allPedagoModules}
                  getOptionLabel={(opt) => `${opt.lib_elp || opt.cod_elp} (${opt.cod_elp})`}
                  isOptionEqualToValue={(option, value) => option.cod_elp === value.cod_elp}
                  value={selectedModules}
                  onChange={(e, val) => handleSelectData(val, [])}
                  renderInput={(params) => <TextField {...params} label="1. Modules" size="small" />}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip variant="outlined" label={option.cod_elp} size="small" {...getTagProps({ index })} />
                    ))
                  }
                />
                <Autocomplete
                  multiple
                  options={availableGroups}
                  isOptionEqualToValue={(option, value) => option.key === value.key}
                  getOptionLabel={(opt) => opt.label}
                  value={selectedGroups}
                  onChange={(e, val) => handleSelectData(selectedModules, val)}
                  disabled={selectedModules.length === 0}
                  renderInput={(params) => <TextField {...params} label="2. Groupes" size="small" />}
                />
                <TextField type="date" label="Date" value={commonDate} onChange={(e) => setCommonDate(e.target.value)} InputLabelProps={{ shrink: true }} size="small" fullWidth />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField type="time" label="Début" value={commonStartTime} onChange={(e) => setCommonStartTime(e.target.value)} InputLabelProps={{ shrink: true }} size="small" fullWidth />
                  <TextField type="time" label="Fin" value={commonEndTime} onChange={(e) => setCommonEndTime(e.target.value)} InputLabelProps={{ shrink: true }} size="small" fullWidth />
                </Box>
                <Divider sx={{ my: 1 }}>Distribution Automatique</Divider>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Autocomplete options={locations} getOptionLabel={(opt) => opt.name} value={rangeStart} onChange={(e, v) => setRangeStart(v)} renderInput={(p) => <TextField {...p} label="De..." size="small" />} sx={{ width: '40%' }} />
                  <Typography variant="body2">-</Typography>
                  <Autocomplete options={locations} getOptionLabel={(opt) => opt.name} value={rangeEnd} onChange={(e, v) => setRangeEnd(v)} renderInput={(p) => <TextField {...p} label="À..." size="small" />} sx={{ width: '40%' }} />
                  <IconButton color="primary" onClick={handleAutoDistribute} disabled={!rangeStart || !rangeEnd}><BalanceIcon /></IconButton>
                </Box>
                {planningSessions.map((session, index) => (
                  <Box key={session.id} sx={{ p: 1.5, border: '1px solid #ddd', borderRadius: 1, position: 'relative', mb: 1 }}>
                    <IconButton size="small" onClick={() => handleRemoveSession(session.id)} sx={{ position: 'absolute', right: 0, top: 0, color: 'error.main' }}><DeleteIcon fontSize="small" /></IconButton>
                    <Grid container spacing={2}>
                      <Grid item xs={7}>
                        <Autocomplete options={locations} getOptionLabel={(opt) => `${opt.name} (${opt.capacity})`} value={session.location} onChange={(e, val) => handleUpdateSession(session.id, 'location', val)} renderInput={(params) => <TextField {...params} label={`Local ${index + 1}`} size="small" />} />
                      </Grid>
                      <Grid item xs={5}>
                        <TextField label="Effectif" type="number" size="small" value={session.count} onChange={(e) => handleUpdateSession(session.id, 'count', e.target.value)} fullWidth />
                      </Grid>
                    </Grid>
                  </Box>
                ))}
                <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={handleAddSession} disabled={planningLoading || allGroupStudents.length === 0}>Ajouter un Local</Button>
                <Button variant="contained" size="large" onClick={handleSubmitPlan} disabled={planningLoading || remainingStudents < 0 || planningSessions.length === 0 || allocatedTotal === 0} sx={{ mt: 2 }}>
                  {remainingStudents > 0 ? `Confirmer (${remainingStudents} non alloués)` : 'Confirmer'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* CALENDAR VIEW */}
        <Grid item xs={12} md={7} sx={{ '@media print': { width: '100% !important', maxWidth: '100% !important', flexBasis: '100% !important' } }}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, '@media print': { boxShadow: 'none', border: 'none' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }} className="no-print">
                  <Typography variant="h6">Planning</Typography>
                  <ToggleButtonGroup value={viewMode} exclusive onChange={(e, v) => v && setViewMode(v)} size="small">
                      <ToggleButton value="calendar"><CalendarIcon sx={{ mr: 1 }}/> Calendrier</ToggleButton>
                      <ToggleButton value="list"><ListIcon sx={{ mr: 1 }}/> Liste</ToggleButton>
                  </ToggleButtonGroup>
              </Box>
              
              {/* ── Selection toolbar ── */}
              {selectedExams.size > 0 && !isArchived && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, px: 2, py: 1, bgcolor: '#e3f2fd', borderRadius: 2, border: '1px solid #1976d2' }} className="no-print">
                  <Checkbox
                    checked={selectedExams.size === exams.length && exams.length > 0}
                    indeterminate={selectedExams.size > 0 && selectedExams.size < exams.length}
                    onChange={e => e.target.checked ? selectAll() : clearSelection()}
                    size="small"
                  />
                  <Typography variant="body2" fontWeight={600} color="primary">
                    {selectedExams.size} examen(s) sélectionné(s)
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Button size="small" onClick={selectAll} disabled={selectedExams.size === exams.length}>
                    Tout sélectionner
                  </Button>
                  <Button size="small" onClick={clearSelection}>
                    Désélectionner
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleBulkDelete}
                    disabled={loading}
                  >
                    Supprimer la sélection
                  </Button>
                </Box>
              )}

              {viewMode === 'calendar' ? (
                <DailyPlanningView
                  exams={exams}
                  onSelectExam={handleSelectExam}
                  selectedExams={selectedExams}
                  onToggleSelect={toggleSelectExam}
                  readOnly={isArchived}
                  onEditExam={handleOpenEdit}
                  sessionLabel={sessionLabel}
                  currentSession={currentSession}
                  globalStats={globalStats}
                  conflictCount={conflictCount}
                />
              ) : (
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #eee', maxHeight: 600 }} className="no-print">
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    size="small"
                                    checked={selectedExams.size === exams.length && exams.length > 0}
                                    indeterminate={selectedExams.size > 0 && selectedExams.size < exams.length}
                                    onChange={e => e.target.checked ? selectAll() : clearSelection()}
                                  />
                                </TableCell>
                                <TableCell><strong>Date</strong></TableCell>
                                <TableCell><strong>Module</strong></TableCell>
                                <TableCell><strong>Groupe</strong></TableCell>
                                <TableCell><strong>Lieu</strong></TableCell>
                                <TableCell><strong>Professeur</strong></TableCell>
                                <TableCell align="center"><strong>Eff.</strong></TableCell>
                                <TableCell align="center"><strong>Action</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {exams.length === 0 ? (
                                <TableRow><TableCell colSpan={8} align="center">Aucun examen programmé.</TableCell></TableRow>
                            ) : (
                                exams.map(e => (
                                    <TableRow
                                      key={e.id}
                                      hover
                                      selected={selectedExams.has(e.id)}
                                      onClick={() => toggleSelectExam([e.id])}
                                      sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell padding="checkbox" onClick={ev => ev.stopPropagation()}>
                                          <Checkbox
                                            size="small"
                                            checked={selectedExams.has(e.id)}
                                            onChange={() => toggleSelectExam([e.id])}
                                          />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">{new Date(e.exam_date.split('T')[0] + 'T00:00:00').toLocaleDateString()}</Typography>
                                            <Typography variant="caption" color="text.secondary">{e.start_time.slice(0,5)} - {e.end_time.slice(0,5)}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{e.module_name}</Typography>
                                            <Typography variant="caption" color="text.secondary">{e.module_code}</Typography>
                                        </TableCell>
                                        <TableCell><Chip label={e.group_name} size="small" sx={{ fontSize: '0.75rem' }} /></TableCell>
                                        <TableCell><Chip icon={<MeetingRoomIcon sx={{ fontSize:'12px !important' }}/>} label={e.location} size="small" variant="outlined" /></TableCell>
                                        <TableCell>
                                            {e.professor_name ? (
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                    {e.professor_name}
                                                </Typography>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                    Non assigné
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="center">{e.assigned_count}</TableCell>
                                        <TableCell align="center" onClick={ev => ev.stopPropagation()}>
                                            {!isArchived && (
                                              <IconButton size="small" color="error" onClick={async () => {
                                                  if(confirm('Supprimer ?')) { await adminAPI.deleteExam(e.id); loadExamsForSession(currentSessionId); }
                                              }}>
                                                  <DeleteIcon fontSize="small" />
                                              </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* DIALOGS */}
        <Dialog open={locationDialog} onClose={() => setLocationDialog(false)} maxWidth="sm" fullWidth className="no-print">
          <DialogTitle>Gestion des Locaux</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 3, display: 'flex', gap: 1 }}>
              <TextField label="Nom (ex: Amphi 1)" size="small" value={newLoc.name} onChange={(e)=>setNewLoc({...newLoc, name:e.target.value})} fullWidth />
              <TextField label="Capacité" type="number" size="small" value={newLoc.capacity} onChange={(e)=>setNewLoc({...newLoc, capacity:e.target.value})} sx={{ width: 100 }} />
              <Button variant="contained" onClick={async () => {
                   if(!newLoc.name || !newLoc.capacity) return;
                   await adminAPI.addLocation(newLoc);
                   const locs = await adminAPI.getLocations();
                   setLocations(locs.sort((a,b)=>a.name.localeCompare(b.name, undefined, {numeric:true, sensitivity:'base'})));
                   setNewLoc({ name: '', capacity: '', type: 'AMPHI' });
              }}>Ajouter</Button>
            </Box>
            <Table size="small">
              <TableHead><TableRow><TableCell>Nom</TableCell><TableCell>Capacité</TableCell><TableCell>Action</TableCell></TableRow></TableHead>
              <TableBody>
                {locations.map(loc => (
                  <TableRow key={loc.id}>
                    <TableCell>{loc.name}</TableCell>
                    <TableCell>{loc.capacity}</TableCell>
                    <TableCell>
                      <IconButton size="small" color="error" onClick={async () => {
                          if(confirm('Supprimer ?')) {
                              await adminAPI.deleteLocation(loc.id);
                              setLocations(prev => prev.filter(l => l.id !== loc.id));
                          }
                      }}><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DialogContent>
          <DialogActions><Button onClick={() => setLocationDialog(false)}>Fermer</Button></DialogActions>
        </Dialog>

        {/* --- EDIT EXAM DIALOG --- */}
        <Dialog
          open={editDialog.open}
          onClose={() => setEditDialog({ open: false, exam: null })}
          maxWidth="sm"
          fullWidth
          className="no-print"
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon color="primary" /> Modifier la planification
          </DialogTitle>
          <DialogContent dividers>
            {editDialog.exam && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                {/* Module name — read-only */}
                <TextField
                  label="Module"
                  value={editDialog.exam.lib_elp_arb || editDialog.exam.module_name || ''}
                  InputProps={{ readOnly: true }}
                  size="small"
                  fullWidth
                  sx={{ bgcolor: '#f5f5f5' }}
                />

                {/* Date */}
                <TextField
                  type="date"
                  label="Date"
                  value={editForm.exam_date}
                  onChange={e => setEditForm(f => ({ ...f, exam_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  fullWidth
                />

                {/* Times */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    type="time"
                    label="Heure début"
                    value={editForm.start_time}
                    onChange={e => setEditForm(f => ({ ...f, start_time: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    type="time"
                    label="Heure fin"
                    value={editForm.end_time}
                    onChange={e => setEditForm(f => ({ ...f, end_time: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    fullWidth
                  />
                </Box>

                {/* Per-room location */}
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                  Locaux ({editForm.locations.length} salle{editForm.locations.length > 1 ? 's' : ''})
                </Typography>
                {editForm.locations.map((loc, i) => (
                  <Autocomplete
                    key={loc.id}
                    options={locations}
                    getOptionLabel={opt => `${opt.name} (${opt.capacity})`}
                    value={locations.find(l => l.name === loc.location) || null}
                    onChange={(e, val) => setEditForm(f => ({
                      ...f,
                      locations: f.locations.map((l, idx) => idx === i ? { ...l, location: val?.name || '' } : l)
                    }))}
                    renderInput={params => (
                      <TextField {...params} label={`Salle ${i + 1}`} size="small" fullWidth />
                    )}
                  />
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialog({ open: false, exam: null })}>Annuler</Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveEdit}
              disabled={loading || !editForm.exam_date || !editForm.start_time || !editForm.end_time}
            >
              Enregistrer
            </Button>
          </DialogActions>
        </Dialog>

        {/* --- STUDENT DIALOG WITH PROFESSOR FORM --- */}
        <Dialog open={studentDialog.open} onClose={() => setStudentDialog(prev => ({ ...prev, open: false }))} maxWidth="md" fullWidth className="no-print">
          
          <DialogContent dividers>
             
             <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
               <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Autocomplete
                  freeSolo
                  options={[...new Set([...AVAILABLE_PROFESSORS, ...dbProfessors.map(p => p.name)])].sort((a, b) => a.localeCompare(b))}
                  value={surveillantName}
                  onInputChange={(e, newVal) => setSurveillantName(newVal)}
                  renderInput={(params) => <TextField {...params} label="Professeur / Surveillant" size="small" />}
                  sx={{ flex: 1 }}
                />
                <Button variant="contained" color="primary" startIcon={<SaveIcon />} onClick={handleSaveSurveillant}>
                  Enregistrer
                </Button>
               </Box>
               {/* Show "Add to list" only when the typed name is not in either list */}
               {surveillantName?.trim() && ![...AVAILABLE_PROFESSORS, ...dbProfessors.map(p => p.name)].some(n => n.toLowerCase() === surveillantName.trim().toLowerCase()) && (
                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 0.5 }}>
                   <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                   <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                     «{surveillantName.trim()}» n'est pas dans la liste.
                   </Typography>
                   <Button
                     size="small"
                     variant="outlined"
                     color="success"
                     startIcon={<AddIcon />}
                     onClick={async () => {
                       try {
                         const added = await adminAPI.addProfessor(surveillantName.trim());
                         setDbProfessors(prev => [...prev, added].sort((a, b) => a.name.localeCompare(b.name)));
                         setSuccess(`"${added.name}" ajouté à la liste des professeurs.`);
                       } catch { setError("Erreur lors de l'ajout du professeur."); }
                     }}
                   >
                     Ajouter à la liste
                   </Button>
                 </Box>
               )}
             </Box>

             {studentDialog.loading ? <CircularProgress /> : (
               <TableContainer sx={{ maxHeight: 400 }}>
                 <Table size="small" stickyHeader>
                   <TableHead>
                     <TableRow>
                       <TableCell>CNE</TableCell><TableCell>Nom</TableCell><TableCell>Prénom</TableCell><TableCell>Signature</TableCell>
                     </TableRow>
                   </TableHead>
                   <TableBody>
                     {studentDialog.students.map(s => (
                       <TableRow key={s.cod_etu}>
                         <TableCell>{s.cod_etu}</TableCell>
                         <TableCell>{s.lib_nom_pat_ind}</TableCell>
                         <TableCell>{s.lib_pr1_ind}</TableCell>
                         <TableCell sx={{ borderBottom:'1px solid #eee' }}></TableCell>
                       </TableRow>
                     ))}
                   </TableBody>
                 </Table>
               </TableContainer>
             )}
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" color="error" startIcon={<PdfIcon />} onClick={handleGeneratePDF}>
                Générer PDF
            </Button>
            <Button onClick={() => setStudentDialog(prev => ({ ...prev, open: false }))}>Fermer</Button>
          </DialogActions>
        </Dialog>

        {/* Sync Progress Dialog */}
        <Dialog open={syncProgressDialog} maxWidth="sm" fullWidth className="no-print"
          onClose={() => { if (syncProgressDone || syncProgressError) setSyncProgressDialog(false); }}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SyncIcon color="primary" /> Synchronisation des listes d'examen
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>

              {/* Step 1 */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {syncProgressStep > 1 && !syncProgressError ? (
                  <CheckCircleIcon color="success" />
                ) : syncProgressStep === 1 && !syncProgressError && !syncProgressDone ? (
                  <CircularProgress size={20} />
                ) : (
                  <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: syncProgressError && syncProgressStep <= 1 ? 'error.main' : 'grey.300' }} />
                )}
                <Box>
                  <Typography variant="body2" fontWeight="bold">Étape 1 — Reconstruction des groupes</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Lecture de la situation pédagogique et recalcul des listes par groupe
                  </Typography>
                </Box>
              </Box>

              {/* Step 2 */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {syncProgressDone && !syncProgressError ? (
                  <CheckCircleIcon color="success" />
                ) : syncProgressStep === 2 && !syncProgressError ? (
                  <CircularProgress size={20} />
                ) : (
                  <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: 'grey.300' }} />
                )}
                <Box>
                  <Typography variant="body2" fontWeight="bold">Étape 2 — Redistribution dans les salles</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Répartition des étudiants selon les capacités des salles
                  </Typography>
                </Box>
              </Box>

              {/* Status message */}
              <Box sx={{ mt: 1 }}>
                {syncProgressError ? (
                  <Alert severity="error">{syncProgressError}</Alert>
                ) : syncProgressDone ? (
                  <Alert severity="success">{syncProgressMsg}</Alert>
                ) : (
                  <Alert severity="info" icon={<CircularProgress size={16} />}>{syncProgressMsg}</Alert>
                )}
              </Box>

            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              disabled={!syncProgressDone && !syncProgressError}
              onClick={() => setSyncProgressDialog(false)}
              variant={syncProgressDone ? 'contained' : 'outlined'}
            >
              {syncProgressDone ? 'Terminé' : 'Fermer'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={conflictDialog.open} onClose={() => setConflictDialog({ ...conflictDialog, open: false })} maxWidth="lg" fullWidth className="no-print">
          <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
             <WarningIcon /> Détails des Conflits ({conflictDialog.conflicts.length})
          </DialogTitle>
          <DialogContent dividers>
             {conflictDialog.loading ? <CircularProgress /> : (
               <TableContainer sx={{ maxHeight: 500 }}>
                 <Table size="small" stickyHeader>
                   <TableHead>
                     <TableRow>
                       <TableCell><strong>Étudiant</strong></TableCell>
                       <TableCell><strong>Date</strong></TableCell>
                       <TableCell sx={{ bgcolor: '#fff3e0' }}><strong>Examen 1</strong></TableCell>
                       <TableCell sx={{ bgcolor: '#ffebee' }}><strong>Examen 2</strong></TableCell>
                     </TableRow>
                   </TableHead>
                   <TableBody>
                     {conflictDialog.conflicts.map((c, i) => (
                       <TableRow key={i} hover>
                         <TableCell>
                           <Typography variant="body2" fontWeight="bold">{c.nom} {c.prenom}</Typography>
                           <Typography variant="caption" color="text.secondary">{c.cod_etu}</Typography>
                         </TableCell>
                         <TableCell>{new Date(c.exam_date.split('T')[0] + 'T00:00:00').toLocaleDateString()}</TableCell>
                         <TableCell sx={{ bgcolor: '#fff3e0' }}>
                           <Typography variant="body2">{c.module1}</Typography>
                           <Typography variant="caption">{c.start1.slice(0,5)} - {c.end1.slice(0,5)} ({c.loc1})</Typography>
                         </TableCell>
                         <TableCell sx={{ bgcolor: '#ffebee' }}>
                           <Typography variant="body2">{c.module2}</Typography>
                           <Typography variant="caption">{c.start2.slice(0,5)} - {c.end2.slice(0,5)} ({c.loc2})</Typography>
                         </TableCell>
                       </TableRow>
                     ))}
                   </TableBody>
                 </Table>
               </TableContainer>
             )}
          </DialogContent>
          <DialogActions>
            <Button color="error" variant="outlined" onClick={() => {
               const csv = ["CNE,Nom,Prenom,Date,Module1,Horaire1,Lieu1,Module2,Horaire2,Lieu2", ...conflictDialog.conflicts.map(c => `${c.cod_etu},"${c.nom}","${c.prenom}",${new Date(c.exam_date.split('T')[0] + 'T00:00:00').toLocaleDateString()},"${c.module1}","${c.start1}-${c.end1}","${c.loc1}","${c.module2}","${c.start2}-${c.end2}","${c.loc2}"`)].join('\n');
               const link = document.createElement("a"); link.href = URL.createObjectURL(new Blob([csv], {type: 'text/csv'}));
               link.download = "conflits_examens.csv"; document.body.appendChild(link); link.click();
            }}>Exporter Conflits</Button>
            <Button onClick={() => setConflictDialog({ ...conflictDialog, open: false })}>Fermer</Button>
          </DialogActions>
        </Dialog>

      </Grid>
    </Box>
  );
};

export default ExamPlanning;